import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { formatShortDate } from '../utils/dates';
import axios from 'axios';
import './MessageWindow.css';
import { Link, useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AlarmClock, ArrowLeft, ArrowRight, Bug, Building2, Check, ChevronDown, ChevronLeft, ChevronRight, CircleCheck,
    CircleDashed, CircleX, Copy, Eye, EyeOff, FileText, Forward, Globe, History, Inbox, Info, LoaderCircle, Mail,
    MessageCircle, MessageSquare, Paperclip, Pencil, Plus, RefreshCw, Send, Shield, Sparkles, StickyNote, Store, Tag,
    Ticket, Trash2, Truck, User, UserPlus, Workflow, X,
} from 'lucide-react';
import { Badge, ConfirmDialog, Drawer, EmptyState, IconChip, Modal, OverflowMenu, Timeline, Toast } from '../design/ui';
import CarriersConversation from './CarriersConversation';
import ManualConversation from './ManualConversation';
import TicketActors from './TicketActors';
import { outsideAuthor, outsideSourceLabel, senderAddress } from '../utils/ticketActors';
import WarehouseConversation, { WAREHOUSE_VALIDATION_STATUSES } from './WarehouseConversation';
import AiRunPanel from './AiRunPanel';
import AiProposalsPanel from './AiProposalsPanel';
import LinkifiedText from './LinkifiedText';
import { fetchStoredSummary, generateSummary, summaryNeedsUpdate } from '../utils/ticketSummary';
import AiTimelineOverlay from './AiTimelineOverlay';
import ColissimoLabels from './ColissimoLabels';
import TicketRefunds from './TicketRefunds';
import TicketWorkspaceMenu from './TicketWorkspaceMenu';
import TicketWorkspaceParcel from './TicketWorkspaceParcel';
import TicketWorkspaceClient from './TicketWorkspaceClient';
import { toolLabel, toolOutcome, prettyJson } from './aiTrace/format';
import { getCarrierFailureTexts } from '../utils/carrierFailureDiagnosis';

import { EDN_STATUS_MAPPING, TERMINAL_READ_STATUS_VALUES, getStatusLabel } from '../constants/ticketStatuses';
import { FileGlyph, fileKind, formatShortDateTime, ticketStatusTone } from './TicketWorkspaceHelpers';
import TicketAgentNotices from './TicketAgentNotices';

const getStoredUserData = () => {
    try {
        const storedUserData = localStorage.getItem('userData') || localStorage.getItem('user_data');
        return storedUserData ? JSON.parse(storedUserData) : null;
    } catch (error) {
        return null;
    }
};

const normalizeEdnTopicOption = (topic) => {
    const topicId = topic?.id ?? topic?.ticket_topic_id ?? topic?.ticket_topic;
    return {
        id: Number(topicId),
        value: String(topicId),
        label: topic?.topic_full || topic?.name || topic?.topic || String(topicId),
        category: topic?.topic || topic?.category || '',
        type: 'edn'
    };
};

// Side panel views (≤ 6, kit § Onglets). Old deep links (?tab=manual|track|
// runs|actors|user) land on the view that now holds them.
const SIDE_TABS = ['carriers', 'warehouse', 'parcel', 'client', 'refunds', 'ai'];
const TAB_ALIASES = { manual: 'carriers', track: 'parcel', runs: 'ai', actors: 'client', user: 'client' };
const tabFromParam = (param) => {
    if (SIDE_TABS.includes(param)) return param;
    return TAB_ALIASES[param] || 'carriers';
};

// The conversation shows its latest entries; older ones fold behind
// « Afficher les messages précédents (n) » — drafts awaiting a decision and
// the message being edited never fold.
const THREAD_KEEP = 8;

const CLOSED_TOAST = { show: false, message: '', success: false };

// Carrier conversation statuses that wait for the team's decision.
const CARRIER_AWAITING_STATUSES = ['pending', 'pending_validation'];

const MessageWindow = () => {
    const { t } = useTranslation();
    const { ticketId } = useParams();
    // Latest ticket id for the WebSocket handler (its closure is created once).
    const ticketIdRef = useRef(ticketId);
    useEffect(() => {
        ticketIdRef.current = ticketId;
    }, [ticketId]);
    // Deep-linkable side-panel view (?tab=carriers|manual|track|runs|actors…), e.g. from the copilot.
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get('tab');

    const channelIcons = {
        Instagram: MessageCircle,
        Messenger: MessageCircle,
        WhatsApp: MessageCircle,
        Gmail: Mail,
        Outlook: Mail,
        Website: Globe,
        Custom: Mail,
        Ticketing: Ticket,
        'ERP API': Ticket
    };
    const [ticket, setTicket] = useState(null);
    // The conversation this ticket continues / the tickets that continue it
    // (a new request after several days on WhatsApp / Messenger / Instagram).
    const [relatedTickets, setRelatedTickets] = useState({ previous: null, next: [] });
    const [messages, setMessages] = useState([]);
    const [employee, setEmployee] = useState('');
    const [aiAgentName, setAiAgentName] = useState('');
    const [trackingCopied, setTrackingCopied] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [editingMessage, setEditingMessage] = useState(null);
    const [editedContent, setEditedContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [userLoading, setUserLoading] = useState(true);
    const [targetLanguage, setTargetLanguage] = useState('original');
    const [translatedMessages, setTranslatedMessages] = useState({});
    const [customerLoading, setCustomerLoading] = useState(false);
    const [orderLoading, setOrderLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [overlayState, setOverlayState] = useState(CLOSED_TOAST);
    const [user, setUser] = useState(null);
    // The shop's customer record (Shopify, ticket.customer_id) and the EDN
    // order (ticket.order): fetched on load, shown in « Client » / « Colis ».
    const [shopCustomer, setShopCustomer] = useState(null);
    const [ednOrder, setEdnOrder] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const [carriersConversation, setCarriersConversation] = useState(null);
    const [validatingMessage, setValidatingMessage] = useState(false);
    const [activeTab, setActiveTab] = useState(() => tabFromParam(requestedTab));
    // « Nouvelle demande » inside the carrier view (was the « Manuel » tab).
    const [showManualForm, setShowManualForm] = useState(requestedTab === 'manual');
    const [showColissimo, setShowColissimo] = useState(requestedTab === 'colissimo');
    const [threadHistoryOpen, setThreadHistoryOpen] = useState(false);
    // AI trace focus: set by the "Run IA" links on AI messages and actions (docs/ai-trace-spec.md §6.2)
    const [aiRunFocus, setAiRunFocus] = useState(null);
    const [aiTimelineOpen, setAiTimelineOpen] = useState(false);
    // Opens the full-screen chronology focused on a run / span (from a message,
    // an AI action, or the « IA » view).
    const openAiRun = (runId, spanId) => {
        setAiRunFocus({ runId: runId || null, spanId: spanId || null, at: Date.now() });
        setAiTimelineOpen(true);
    };
    const closeAiTimeline = useCallback(() => setAiTimelineOpen(false), []);
    const threadRef = useRef(null);
    // KI-225: decorative images (signatures, logos) are kept, folded away.
    const [showDecorativeFiles, setShowDecorativeFiles] = useState(false);
    const [expandedDecorative, setExpandedDecorative] = useState({});
    // KI-229: the quoted thread of an e-mail, folded under the sender's words.
    const [expandedQuotedHistory, setExpandedQuotedHistory] = useState({});

    // Employee assignment
    const [employees, setEmployees] = useState([]);
    const [assigningTicket, setAssigningTicket] = useState(false);

    // State for ticket files
    const [ticketFiles, setTicketFiles] = useState([]);
    const [messageFiles, setMessageFiles] = useState({});
    // KI-225: the stored objects the platform judged decorative (signature,
    // logo, icon) — a message's copy of the file is matched on its object.
    const decorativeUrls = useMemo(() => new Set(
        ticketFiles.filter((file) => file?.metadata?.file_class === 'decorative').map((file) => file?.s3_url).filter(Boolean)
    ), [ticketFiles]);
    const isDecorativeFile = useCallback((file) => (
        file?.metadata?.file_class === 'decorative' || Boolean(file?.s3_url && decorativeUrls.has(file.s3_url))
    ), [decorativeUrls]);
    const documentTicketFiles = useMemo(() => ticketFiles.filter((file) => !isDecorativeFile(file)), [ticketFiles, isDecorativeFile]);
    const decorativeTicketFiles = useMemo(() => ticketFiles.filter((file) => isDecorativeFile(file)), [ticketFiles, isDecorativeFile]);
    // State for file overlay
    const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(null);

    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const pingIntervalRef = useRef(null);
    const fetchMessagesRef = useRef(null);
    const latestClientEmailRef = useRef('');
    const textareaRef = useRef(null);
    const lastFetchedTicketIdRef = useRef(null); // Prevent duplicate fetches
    const presenceIntervalRef = useRef(null); // For presence heartbeat
    const activePresenceTicketRef = useRef(null);
    const presenceCloseTimeoutsRef = useRef({});

    // Context data (GET /context)
    const [contextData, setContextData] = useState(null);
    const [contextLoading, setContextLoading] = useState(true);
    const [clientTickets, setClientTickets] = useState([]);
    const [clientTicketsLoading, setClientTicketsLoading] = useState(false);
    const [clientTicketsError, setClientTicketsError] = useState(null);

    // Warehouse conversation (EDN API accounts): its state feeds « À vérifier ».
    const [warehouseConversation, setWarehouseConversation] = useState(null);
    // Pending AI proposals of the ticket (GET /ai-proposals → pending), for « À vérifier ».
    const [pendingProposals, setPendingProposals] = useState(0);

    // File upload
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const fileInputRef = useRef(null);

    const attachmentList = useMemo(() => {
        const ticketAttachments = ticketFiles.map((file, index) => ({
            file,
            source: 'ticket',
            parentId: ticketId,
            order: index,
        }));

        const messageAttachments = messages.reduce((acc, message) => {
            const files = messageFiles[message._id] || [];
            files.forEach((file, index) => {
                acc.push({
                    file,
                    source: 'message',
                    parentId: message._id,
                    order: index,
                    createdAt: message.created_at,
                });
            });
            return acc;
        }, []);

        return [...ticketAttachments, ...messageAttachments];
    }, [ticketFiles, messageFiles, messages, ticketId]);

    const selectedAttachment =
        selectedAttachmentIndex !== null ? attachmentList[selectedAttachmentIndex] : null;
    const selectedFile = selectedAttachment?.file || null;
    const totalAttachments = attachmentList.length;

    // Notes
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [noteContent, setNoteContent] = useState('');
    const [selectedMessageForNote, setSelectedMessageForNote] = useState(null);
    const [addingNote, setAddingNote] = useState(false);

    // Writing assistant
    const [gettingAiHelp, setGettingAiHelp] = useState(false);

    // Which draft message is being sent
    const [sendingDraftMessageId, setSendingDraftMessageId] = useState(null);
    // Drafts whose held carrier contacts the operator chose NOT to send with
    // the reply (KI-108); by default they travel with it.
    const [skipDraftCarrierContacts, setSkipDraftCarrierContacts] = useState({});
    const [ednVisibilityModal, setEdnVisibilityModal] = useState({
        show: false,
        action: null,
        messageId: null
    });
    // A reply whose prepared carrier contact will not leave with it (409):
    // the operator confirms before it is sent alone (was window.confirm).
    const [notLeavingConfirm, setNotLeavingConfirm] = useState(null);
    // The draft the operator asked to delete, until confirmed.
    const [draftToDelete, setDraftToDelete] = useState(null);

    // Draft message file attachments
    const [draftPendingFiles, setDraftPendingFiles] = useState({});
    const [activeDraftForFileUpload, setActiveDraftForFileUpload] = useState(null);
    const draftFileInputRef = useRef(null);

    // AI handoff
    const [showHandoffModal, setShowHandoffModal] = useState(false);
    const [handoffInstructions, setHandoffInstructions] = useState('');
    const [handingOffToAI, setHandingOffToAI] = useState(false);
    const [expandedHandoffMessages, setExpandedHandoffMessages] = useState({});

    // AI action details (per step)
    const [expandedAiActions, setExpandedAiActions] = useState({});

    // Note expansion per message
    const [expandedNotes, setExpandedNotes] = useState({});

    // Slack message
    const [showSlackModal, setShowSlackModal] = useState(false);
    const [slackMessage, setSlackMessage] = useState('');
    const [sendingSlackMessage, setSendingSlackMessage] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);

    // Bug report
    const [showBugReportModal, setShowBugReportModal] = useState(false);
    const [bugDescription, setBugDescription] = useState('');
    const [sendingBugReport, setSendingBugReport] = useState(false);

    // E-mail forwarding
    const [showForwardModal, setShowForwardModal] = useState(false);
    const [forwardToInput, setForwardToInput] = useState('');
    const [forwardCcInput, setForwardCcInput] = useState('');
    const [forwardComment, setForwardComment] = useState('');
    const [forwardingEmail, setForwardingEmail] = useState(false);

    const [hideDraftMessages, setHideDraftMessages] = useState(false);

    // Brand info
    const [brand, setBrand] = useState(null);

    // Pending files to be sent with the message
    const [pendingFiles, setPendingFiles] = useState([]);

    // Topic editing
    const [availableTopics, setAvailableTopics] = useState([]);
    const [updatingTopic, setUpdatingTopic] = useState(false);
    const [topicsLoading, setTopicsLoading] = useState(false);

    // The ticket client picker (a client never identified at intake blocks the
    // client-mapped carrier portal accounts — KI-65)
    const [clientOptions, setClientOptions] = useState([]);
    const [clientOptionsLoaded, setClientOptionsLoaded] = useState(false);
    const [clientOptionsLoading, setClientOptionsLoading] = useState(false);
    const [updatingClient, setUpdatingClient] = useState(false);
    const [clientFilter, setClientFilter] = useState('');

    // EDN status editing
    const [availableEdnStatuses, setAvailableEdnStatuses] = useState([]);
    const [ednStatusesLoading, setEdnStatusesLoading] = useState(false);
    const [updatingEdnStatus, setUpdatingEdnStatus] = useState(false);

    // Ticket summary
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [ticketSummary, setTicketSummary] = useState('');
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [showStatusHistoryModal, setShowStatusHistoryModal] = useState(false);
    const [statusHistoryData, setStatusHistoryData] = useState(null);
    const [loadingStatusHistory, setLoadingStatusHistory] = useState(false);

    // Toasts: a success closes itself after 5 s, an error stays until closed
    // (kit § Retours). Every former overlay timer goes through this.
    const hideToastLater = () => {
        setTimeout(() => setOverlayState((previous) => (previous.success ? CLOSED_TOAST : previous)), 5000);
    };

    // Another ticket opened in the same window (a related ticket's link):
    // its conversation and its carrier view start folded again.
    const shownTicketRef = useRef(ticketId);
    useEffect(() => {
        if (shownTicketRef.current === ticketId) return;
        shownTicketRef.current = ticketId;
        setThreadHistoryOpen(false);
        setShowManualForm(false);
    }, [ticketId]);

    // The conversation opens on its latest entries (a draft awaiting
    // validation sits at the bottom, by the composer).
    useEffect(() => {
        if (loading) return;
        const node = threadRef.current;
        if (node) node.scrollTop = node.scrollHeight;
    }, [loading, ticketId]);

    const selectTab = (tab) => {
        setActiveTab(tab);
        if (tab !== 'carriers') setShowManualForm(false);
        const next = new URLSearchParams(searchParams);
        next.set('tab', tab);
        setSearchParams(next, { replace: true });
    };

    const fetchProposalCount = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/ai-proposals`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const pending = response?.data?.pending;
            setPendingProposals(typeof pending === 'number' ? pending : 0);
        } catch (error) {
            setPendingProposals(0);
        }
    };

    const fetchTicketContext = async () => {
        setContextLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/context`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setContextData(response.data);
        } catch (error) {
        } finally {
            setContextLoading(false);
        }
    };

    const fetchAvailableTopics = async () => {
        setTopicsLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            if (isEdnCustomUser) {
                const response = await axios.get('/api/edn_tms/edn_tms/ticket/topics', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setAvailableTopics((response.data?.topics || []).map(normalizeEdnTopicOption));
            } else {
                const response = await axios.get('/api/tickets/available-topics', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const segmentOptions = (response.data.all_topics || []).map((topic) => ({
                    ...topic,
                    kind: 'segment',
                }));
                // Standard classification topics (independent axis: they never
                // overwrite the routing topic) when the tenant enabled them.
                let classificationOptions = [];
                try {
                    const configResponse = await axios.get('/api/logistics/topics-config', {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (configResponse.data?.classification_enabled) {
                        classificationOptions = (configResponse.data.catalog || []).map((entry) => ({
                            value: entry.slug,
                            label: entry.name,
                            kind: 'classification',
                        }));
                    }
                } catch (configError) {
                    // Employees may not have access to the config endpoint; the
                    // segment options alone are still valid.
                }
                setAvailableTopics([...segmentOptions, ...classificationOptions]);
            }
        } catch (error) {
            console.error('Failed to fetch available topics:', error);
        } finally {
            setTopicsLoading(false);
        }
    };

    const handleUpdateTopic = async (topicOption) => {
        const newTopic = typeof topicOption === 'object' ? topicOption.value : topicOption;
        if (!newTopic || updatingTopic) return;

        // Classification options set what the ticket is ABOUT
        // (topic_category/topic_name) and never touch the routing topic.
        const isClassificationOption = !isEdnCustomUser
            && typeof topicOption === 'object'
            && topicOption.kind === 'classification';

        setUpdatingTopic(true);
        try {
            const token = localStorage.getItem('access_token');
            let updatedTopicLabel = newTopic;
            let updatedTopicCategory = newTopic;
            let updatedTopicId = null;

            if (isEdnCustomUser) {
                updatedTopicId = Number(topicOption?.id ?? newTopic);
                const response = await axios.put('/api/edn_tms/edn_tms/ticket/topic',
                    { ticket_id: ticketId, ticket_topic_id: updatedTopicId },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const ticketTopic = response.data?.ticket_topic || {};
                updatedTopicLabel = ticketTopic.topic_full || topicOption?.label || newTopic;
                updatedTopicCategory = ticketTopic.topic || topicOption?.category || updatedTopicLabel;
            } else if (isClassificationOption) {
                const response = await axios.put(`/api/tickets/${ticketId}/topic-classification`,
                    { topic: newTopic },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                updatedTopicCategory = response.data?.topic_category || newTopic;
                updatedTopicLabel = response.data?.topic_name || topicOption?.label || newTopic;
            } else {
                await axios.put(`/api/tickets/${ticketId}/topic`,
                    { topic: newTopic },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }

            const applyEdnOrClassif = isEdnCustomUser || isClassificationOption;

            if (contextData) {
                setContextData(prev => ({
                    ...prev,
                    shipping_info: {
                        ...prev.shipping_info,
                        topic: applyEdnOrClassif ? updatedTopicCategory : newTopic,
                        topic_id: isEdnCustomUser ? updatedTopicId : prev.shipping_info?.topic_id,
                        topic_name: applyEdnOrClassif ? updatedTopicLabel : prev.shipping_info?.topic_name
                    }
                }));
            }

            setTicket(prev => prev ? {
                ...prev,
                // The routing topic only changes when a segment option is picked
                topic: applyEdnOrClassif ? prev.topic : newTopic,
                topic_id: isEdnCustomUser ? updatedTopicId : prev.topic_id,
                ticket_topic_id: isEdnCustomUser ? updatedTopicId : prev.ticket_topic_id,
                topic_name: applyEdnOrClassif ? updatedTopicLabel : prev.topic_name,
                topic_category: applyEdnOrClassif ? updatedTopicCategory : prev.topic_category,
                ticket_topic: applyEdnOrClassif ? {
                    ...(prev.ticket_topic || {}),
                    id: isEdnCustomUser ? updatedTopicId : prev.ticket_topic?.id,
                    ticket_topic_id: isEdnCustomUser ? updatedTopicId : prev.ticket_topic?.ticket_topic_id,
                    name: updatedTopicLabel,
                    topic: updatedTopicCategory,
                    topic_full: updatedTopicLabel,
                    category: updatedTopicCategory
                } : prev.ticket_topic
            } : prev);
            
            setOverlayState({
                show: true,
                message: t('messageWindow.topicUpdateSuccess', 'Topic updated successfully'),
                success: true
            });
        } catch (error) {
            const errorMessage = error.response?.data?.detail || t('messageWindow.topicUpdateError', 'Failed to update topic');
            setOverlayState({
                show: true,
                message: errorMessage,
                success: false
            });
        } finally {
            setUpdatingTopic(false);
            hideToastLater();
        }
    };

    const fetchClientOptions = async () => {
        setClientOptionsLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/client-options`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setClientOptions(response.data?.clients || []);
            setClientOptionsLoaded(true);
        } catch (error) {
            console.error('Failed to fetch client options:', error);
        } finally {
            setClientOptionsLoading(false);
        }
    };

    // Sets the ticket's client only: no enrichment re-run, no AI restart. A
    // carrier request refused for « no identified client » goes through once
    // approved again (the portal account is chosen from this client).
    const handleSelectClient = async (clientOption) => {
        if (!clientOption?.id || updatingClient) return;
        setUpdatingClient(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.put(`/api/tickets/${ticketId}/client`,
                { client_id: clientOption.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const client = response.data?.client || {};
            setContextData(prev => prev ? {
                ...prev,
                client_info: {
                    ...(prev.client_info || {}),
                    id: client.id,
                    name: client.name || clientOption.name,
                    source: client.source || 'operator',
                },
            } : prev);
            setOverlayState({
                show: true,
                message: t('messageWindow.clientUpdateSuccess', 'Client set on the ticket'),
                success: true
            });
        } catch (error) {
            const detail = error.response?.data?.detail;
            setOverlayState({
                show: true,
                message: typeof detail === 'string' ? detail : t('messageWindow.clientUpdateError', 'Failed to set the client'),
                success: false
            });
        } finally {
            setUpdatingClient(false);
            hideToastLater();
        }
    };

    const fetchEdnStatuses = async () => {
        setEdnStatusesLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get('/api/edn_tms/edn_tms/status', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAvailableEdnStatuses(response.data?.status_list || EDN_STATUS_MAPPING);
        } catch (error) {
            setAvailableEdnStatuses(EDN_STATUS_MAPPING);
        } finally {
            setEdnStatusesLoading(false);
        }
    };

    const handleUpdateEdnStatus = async (newStatus) => {
        if (!newStatus || updatingEdnStatus || newStatus === ticket?.status) return;

        setUpdatingEdnStatus(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.put('/api/edn_tms/edn_tms/ticket/status',
                { ticket_id: ticketId, status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setTicket(prev => prev ? { ...prev, status: newStatus } : prev);
            setOverlayState({
                show: true,
                message: t('messageWindow.statusUpdateSuccess', 'Status updated successfully'),
                success: true
            });
        } catch (error) {
            const errorMessage = error.response?.data?.detail || t('messageWindow.statusUpdateError', 'Failed to update status');
            setOverlayState({
                show: true,
                message: errorMessage,
                success: false
            });
        } finally {
            setUpdatingEdnStatus(false);
            hideToastLater();
        }
    };

    const fetchCarriersConversation = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/carriers_conversation/${ticketId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCarriersConversation(response.data);
        } catch (error) {
        }
    };

    const fetchWarehouseConversation = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/warehouse_conversation/${ticketId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWarehouseConversation(response.data);
        } catch (error) {
        }
    };

    const connectWebSocket = () => {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        // Close any previous socket before opening a new one: a leaked
        // handler that keeps reconnecting stacks live connections server-side
        // (one dashboard was observed holding 15+ sockets on one worker).
        if (wsRef.current) {
            wsRef.current.onclose = null;
            wsRef.current.close();
        }

        const ws = new WebSocket(`/api/ws?token=${token}`);
        wsRef.current = ws;

        ws.onopen = (event) => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        ws.onclose = (event) => {
            // Reconnect only if this socket is still the active one — after
            // unmount or replacement the ref no longer points at it.
            if (wsRef.current !== ws) return;
            // The server closes with 1008 when the token is expired/invalid:
            // retrying every 5 s with the same token can never succeed (one
            // stale tab produced ~100 rejected handshakes in 12 minutes on
            // 2026-09-04). Same handling as Dashboard.js: drop the session.
            const reason = (event.reason || "").toLowerCase();
            if (event.code === 1008 || event.code === 4000 || event.code === 4001 ||
                reason.includes("token") || reason.includes("auth")) {
                console.log("WebSocket closed due to authentication issue, not reconnecting");
                localStorage.removeItem("access_token");
                navigate("/");
                return;
            }
            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        };

        ws.onmessage = (event) => {
            if (event.data === 'ping') {
                ws.send('pong');
            } else {
                // Try to parse as JSON for presence updates
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'ticket_presence_update') {
                        // Presence updates are handled separately, no action needed here
                        // The Dashboard handles updating the ticket list presence
                        return;
                    }
                    // Ticket-scoped events (ticket_created / ticket_updated /
                    // message_created carry ticket_id): only the open ticket's
                    // conversation needs refetching. Every tenant-wide event used
                    // to trigger a full messages fetch in every open window.
                    if (data.ticket_id && ticketIdRef.current && String(data.ticket_id) !== String(ticketIdRef.current)) {
                        return;
                    }
                } catch (e) {
                    // Not JSON, treat as regular refresh message
                }
                // Fetch messages using the latest ticket context
                fetchMessagesRef.current?.();
            }
        };
    };

    useEffect(() => {
        const initialLoad = async () => {
            await fetchUserData(); // Fetches and sets user
            // Other non-ticket specific initial fetches can go here if any
        };
        initialLoad();

        // WebSocket and ping interval setup will be handled by the useEffect below, dependent on 'user'
        
        return () => {
            // General cleanup for the component if any, not specific to WebSocket anymore here
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Runs once on component mount

    // New useEffect to handle WebSocket connection based on user or ticket
    useEffect(() => {
        if (user) {
            connectWebSocket();
            pingIntervalRef.current = setInterval(() => {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    wsRef.current.send('ping');
                }
            }, 30000);
        }

        return () => {
            // Cleanup WebSocket and ping interval if they were initialized
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, ticketId]); // Runs when 'user' or 'ticketId' state changes

    // Add new useEffect to fetch employees when user is available
    useEffect(() => {
        if (user) {
            fetchEmployees();
        }
    }, [user]);

    // Presence management - drives "who is viewing" + "time spent on ticket" metric.
    // Open once for the current ticket, refresh with heartbeats, and only close on
    // real exits so rerenders do not create artificial visit sessions.
    useEffect(() => {
        if (!ticketId || !user?._id) return;

        const token = localStorage.getItem('access_token');
        if (!token) return;

        if (presenceCloseTimeoutsRef.current[ticketId]) {
            clearTimeout(presenceCloseTimeoutsRef.current[ticketId]);
            delete presenceCloseTimeoutsRef.current[ticketId];
        }

        const markPresence = async () => {
            try {
                await axios.post(
                    `/api/tickets/${ticketId}/presence`,
                    { is_active: true },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (error) {
                console.error('Failed to mark presence:', error);
            }
        };

        const startHeartbeat = () => {
            if (presenceIntervalRef.current) return;
            presenceIntervalRef.current = setInterval(markPresence, 60000);
        };

        const stopHeartbeat = () => {
            if (presenceIntervalRef.current) {
                clearInterval(presenceIntervalRef.current);
                presenceIntervalRef.current = null;
            }
        };

        const clearPresence = async () => {
            try {
                await axios.delete(`/api/tickets/${ticketId}/presence`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (error) {
                console.error('Failed to clear presence:', error);
            }
        };

        const schedulePresenceClose = () => {
            if (presenceCloseTimeoutsRef.current[ticketId]) {
                clearTimeout(presenceCloseTimeoutsRef.current[ticketId]);
            }

            const timeoutId = setTimeout(() => {
                clearPresence();
                delete presenceCloseTimeoutsRef.current[ticketId];
            }, 1000);

            presenceCloseTimeoutsRef.current[ticketId] = timeoutId;
        };

        // sendBeacon does not support DELETE and cannot attach Authorization headers.
        // POST {is_active:false} is the documented fallback for tab-close / reload.
        const beaconClose = () => {
            try {
                const blob = new Blob(
                    [JSON.stringify({ is_active: false })],
                    { type: 'application/json' }
                );
                navigator.sendBeacon(`/api/tickets/${ticketId}/presence`, blob);
            } catch (error) {
                console.error('Failed to beacon-close presence:', error);
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                stopHeartbeat();
            } else if (document.visibilityState === 'visible') {
                markPresence();
                startHeartbeat();
            }
        };

        const handlePageHide = () => {
            stopHeartbeat();
            beaconClose();
            activePresenceTicketRef.current = null;
        };

        markPresence();
        activePresenceTicketRef.current = ticketId;
        startHeartbeat();

        window.addEventListener('pagehide', handlePageHide);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            stopHeartbeat();
            window.removeEventListener('pagehide', handlePageHide);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            // SPA route change: close ticket A before ticket B mounts.
            schedulePresenceClose();
            if (activePresenceTicketRef.current === ticketId) {
                activePresenceTicketRef.current = null;
            }
        };
    }, [ticketId, user?._id]);

    // This useEffect will run when ticketId changes, or when the user state is updated
    // (e.g., after fetchUserData completes).
    // Uses ref to prevent duplicate fetches for the same ticket
    useEffect(() => {
        if (user !== undefined && ticketId) { // user can be null (failed login) or an object
            // Only fetch if we haven't fetched this ticket already
            if (lastFetchedTicketIdRef.current !== ticketId) {
                lastFetchedTicketIdRef.current = ticketId;
                fetchTicketAndMessages();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId, user]);

    const fetchUserData = async () => {
        setUserLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                 setUser(null); 
                 setUserLoading(false);
                 navigate('/'); 
                 return;
            }
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            setUser(null); 
            if (error.response && (error.response.status === 401 || error.response.status === 422)) {
                localStorage.removeItem('access_token');
                navigate('/');
            }
        } finally {
            setUserLoading(false);
        }
    };

    const fetchTicketAndMessages = async () => {
        setLoading(true);
        setRelatedTickets({ previous: null, next: [] });
        const token = localStorage.getItem('access_token');

        try {
            if (!token) {
                setTicket(null);
                setMessages([]);
                setLoading(false);
                navigate('/');
                return;
            }

            const response = await axios.get(`/api/tickets/${ticketId}/messages`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data && response.data.ticket) {
                setTicket(response.data.ticket);
                setRelatedTickets({
                    previous: response.data.related_tickets?.previous || null,
                    next: response.data.related_tickets?.next || [],
                });
                setMessages(response.data.messages || []);
                setEmployee(response.data.employee || 'N/A');
                setAiAgentName(response.data.ai_agent_name || '');
                setBrand(response.data.brand || null);
                
                fetchOrderAndCustomerInfo(response.data.ticket, token);

                // Fetch additional data in parallel for better performance
                await Promise.all([
                    fetchTicketContext(),
                    fetchProposalCount(),
                    fetchCarriersConversation(),
                    user?.edn_api_account ? fetchWarehouseConversation() : Promise.resolve(),
                    fetchTicketFiles()
                ]);
            } else {
                setTicket(null);
                setMessages([]);
            }
            setLoading(false);
            
        } catch (error) {
            setTicket(null); 
            setMessages([]);
            setLoading(false);
        }
    };

    const fetchMessages = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/messages`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMessages(response.data.messages);
        } catch (error) {
        }
    };

    // Keep a ref to the latest fetchMessages so the WebSocket uses the current ticket
    useEffect(() => {
        fetchMessagesRef.current = fetchMessages;
    });

    const fetchOrderAndCustomerInfo = async (ticketData, token) => {
        // The shop's customer record (Shopify) and the EDN order behind the
        // ticket: shown in « Client » and « Colis ».
        if (ticketData.customer_id) {
            fetchCustomerInfo(ticketData.customer_id, token);
        } else {
            setShopCustomer(null);
        }

        if (ticketData.order) {
            fetchOrderInfo(ticketData.order, token);
        } else {
            setEdnOrder(null);
        }
    };

    // New function to fetch ticket files
    const fetchTicketFiles = async () => {
        setMessageFiles({});
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/tickets/${ticketId}/files`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const ticketLevelFiles = response.data?.files || [];
            const messageFilePayload = response.data?.message_files || [];
            
            const messageFileMap = messageFilePayload.reduce((acc, entry) => {
                if (entry?.message_id && Array.isArray(entry.files)) {
                    acc[entry.message_id] = entry.files;
                }
                return acc;
            }, {});

            setTicketFiles(ticketLevelFiles);
            setMessageFiles(messageFileMap);
        } catch (error) {
            setMessageFiles({});
        }
    };

    const fetchClientTickets = useCallback(
        async (email) => {
            try {
                const token = localStorage.getItem('access_token');
                if (!token) {
                    if (latestClientEmailRef.current !== email) {
                        return;
                    }

                    setClientTickets([]);
                    setClientTicketsError(t('messageWindow.clientTicketsLoadError'));
                    return;
                }

                const response = await axios.get('/api/clients/tickets/by-email', {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        client_email: email,
                        limit: 20,
                    },
                });

                if (latestClientEmailRef.current !== email) {
                    return;
                }

                setClientTickets(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                if (latestClientEmailRef.current !== email) {
                    return;
                }

                const fallbackMessage =
                    error?.response?.data?.detail || t('messageWindow.clientTicketsLoadError');

                setClientTickets([]);
                setClientTicketsError(fallbackMessage);
            } finally {
                if (latestClientEmailRef.current === email) {
                    setClientTicketsLoading(false);
                }
            }
        },
        [t]
    );

    const adjustTextareaHeight = useCallback(() => {
        if (!textareaRef.current) return;
        const textarea = textareaRef.current;
        textarea.style.height = '72px';
        const nextHeight = Math.min(Math.max(textarea.scrollHeight, 72), 260);
        textarea.style.height = `${nextHeight}px`;
    }, []);

    const handleComposerClick = useCallback((event) => {
        if (!textareaRef.current) return;
        const target = event.target;
        if (target?.closest && target.closest('.tkw-composer__bar, .tkw-file__remove')) {
            return;
        }
        textareaRef.current.focus();
    }, []);

    const getHandoffPreview = useCallback(
        (content) => {
            const normalized = (content || '').trim();
            if (!normalized) {
                return t('messageWindow.noHandoffContent', 'No handoff instructions provided.');
            }
            // Get first line only for collapsed preview
            const firstLine = normalized.split('\n')[0];
            if (firstLine.length <= 80) {
                return firstLine;
            }
            return `${firstLine.slice(0, 80)}…`;
        },
        [t]
    );

    const normalizeMessageContent = (content) => (typeof content === 'string' ? content : '');

    // Message text: one line per <br />, http(s):// and www. addresses clickable (LinkifiedText).
    const formatMessageContent = (content) => <LinkifiedText text={normalizeMessageContent(content)} />;
    
    const fetchCustomerInfo = async (customerId, token) => {
        setCustomerLoading(true);
        try {
            const response = await axios.get(`/api/shopify/get_customer`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { customer_id: customerId }
            });
            setShopCustomer(response.data && typeof response.data === 'object' ? response.data : null);
        } catch (error) {
        } finally {
            setCustomerLoading(false);
        }
    };

    const fetchOrderInfo = async (orderId, token) => {
        setOrderLoading(true);
        try {
            const response = await axios.get(`/api/logistics/edn/order_by_id/${orderId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEdnOrder(response.data && typeof response.data === 'object' ? response.data : null);
        } catch (error) {
        } finally {
            setOrderLoading(false);
        }
    };

    const openEdnVisibilityModal = (action, messageId = null) => {
        setEdnVisibilityModal({
            show: true,
            action,
            messageId
        });
    };

    const closeEdnVisibilityModal = () => {
        setEdnVisibilityModal({
            show: false,
            action: null,
            messageId: null
        });
    };

    const handleEdnVisibilitySelect = (visibility) => {
        const pendingAction = ednVisibilityModal.action;
        const pendingMessageId = ednVisibilityModal.messageId;
        closeEdnVisibilityModal();

        if (pendingAction === 'new-message') {
            executeSendMessage(visibility);
            return;
        }

        if (pendingAction === 'draft-message' && pendingMessageId) {
            executeSendDraftMessage(pendingMessageId, visibility);
        }
    };

    const handleSendMessage = () => {
        if (!newMessage.trim() && pendingFiles.length === 0) return;
        if (sendingMessage) return;

        if (isEdnCustomUser) {
            openEdnVisibilityModal('new-message');
            return;
        }

        executeSendMessage('all');
    };

    const executeSendMessage = async (visibility = 'all') => {
        if (!newMessage.trim() && pendingFiles.length === 0) return;
        if (sendingMessage) return;
        
        setSendingMessage(true);
        try {
            const token = localStorage.getItem('access_token');
            
            const payload = {
                ticket_id: ticketId,
                content: newMessage,
            };

            if (isEdnCustomUser) {
                payload.visibility = visibility;
            }
            
            // Include files if any are pending
            if (pendingFiles.length > 0) {
                payload.files = pendingFiles.map(f => ({
                    name: f.name,
                    mime_type: f.mime_type,
                    content: f.content
                }));
            }
            
            await axios.post('/api/messages/add', payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            
            setNewMessage('');
            setPendingFiles([]); // Clear pending files after successful send
            fetchMessages();
            
            // Refresh files list if files were sent
            if (pendingFiles.length > 0) {
                fetchTicketFiles();
            }
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.sendError', 'Failed to send message'),
                success: false
            });
            hideToastLater();
        } finally {
            setSendingMessage(false);
        }
    };

    const handleGetAssistantResponse = async () => {
        setGettingAiHelp(true);
        try {
            const token = localStorage.getItem('access_token');
            const aiLogsString = JSON.stringify(ticket.ai_logs);

            const response = await axios.post('/api/messages/writer_assistant', {
                text: newMessage,
                ai_logs: aiLogsString,
                ticket_id: ticketId,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // KI-84: when the assistant could not produce a message (it answered
            // with commentary instead), the backend returns the draft unchanged
            // with rewritten=false — keep the operator's text and say so.
            if (response.data.rewritten === false) {
                setOverlayState({
                    show: true,
                    message: t('messageWindow.aiHelpKept', 'The assistant could not rewrite this draft; your text was kept.'),
                    success: false,
                });
                hideToastLater();
                return;
            }
            setNewMessage(response.data.response);
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.aiHelpFailed', 'The writing assistant is unavailable; your draft was kept.'),
                success: false,
            });
            hideToastLater();
        } finally {
            setGettingAiHelp(false);
        }
    };

    const redirectWithNotification = (message, success) => {
        const storedSearch = sessionStorage.getItem('una_dashboard_search');
        const dashboardSearch = location.state?.fromDashboardSearch
            || storedSearch
            || '';
        if (storedSearch) {
            sessionStorage.removeItem('una_dashboard_search');
        }
        navigate(`/dashboard${dashboardSearch}`, {
            state: {
                notification: {
                    message,
                    success
                }
            }
        });
    };

    const handleResolveTicket = async () => {
        try {
            const token = localStorage.getItem('access_token');
            await axios.put(`/api/tickets/${ticketId}/resolve`, null, {
                headers: { Authorization: `Bearer ${token}` },
            });
            redirectWithNotification(t('messageWindow.resolveSuccess'), true);
        } catch (error) {
            redirectWithNotification(t('messageWindow.resolveError'), false);
        }
    };
    
    // The stored summary first (28/10): shown when current and in the person's
    // language, generated (and stored) otherwise or on « Regenerate ».
    const handleGetTicketSummary = async (regenerate = false) => {
        setShowSummaryModal(true);
        setLoadingSummary(true);
        setTicketSummary('');
        try {
            const stored = regenerate === true ? null : await fetchStoredSummary(ticketId).catch(() => null);
            const current = stored && stored.summary && !summaryNeedsUpdate(stored)
                ? stored : await generateSummary(ticketId);
            setTicketSummary(current.summary || '');
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.summaryError', 'Failed to generate summary'),
                success: false
            });
            hideToastLater();
            setShowSummaryModal(false);
        } finally {
            setLoadingSummary(false);
        }
    };

    const getEdnStatusLabel = (statusValue) => {
        if (!statusValue) return t('messageWindow.unknownStatus', 'Unknown');
        const statusOption = EDN_STATUS_MAPPING.find((status) => status.value === statusValue);
        return statusOption?.name || getStatusLabel(t, statusValue);
    };

    const handleGetStatusHistory = async () => {
        if (!isEdnCustomUser || loadingStatusHistory) return;

        setShowStatusHistoryModal(true);
        setLoadingStatusHistory(true);
        setStatusHistoryData(null);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`/api/edn_tms/edn_tms/ticket/${ticketId}/status-history`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setStatusHistoryData(response.data);
        } catch (error) {
            const errorMessage = error.response?.data?.detail || t('messageWindow.statusHistoryError', 'Failed to load status history');
            setOverlayState({
                show: true,
                message: errorMessage,
                success: false
            });
            hideToastLater();
            setShowStatusHistoryModal(false);
        } finally {
            setLoadingStatusHistory(false);
        }
    };

    const handleSendDraftMessage = (messageId) => {
        if (sendingDraftMessageId === messageId) return;

        if (isEdnCustomUser) {
            openEdnVisibilityModal('draft-message', messageId);
            return;
        }

        executeSendDraftMessage(messageId, 'all');
    };

    // The carrier contact the AI prepared with this reply will not leave with it
    // (refused, unticked, failed…): the reply may announce it (backend 409,
    // draft_carrier_contacts.contacts_not_leaving). The operator confirms first.
    const notLeavingLines = (detail) => {
        const contacts = Array.isArray(detail?.contacts) ? detail.contacts : [];
        return contacts.map((contact) => `${contact.carrier_name || '?'} : ${t(
            `messageWindow.carrierContactNotLeaving.${contact.reason}`, contact.reason || '')}`).join(' · ');
    };

    const executeSendDraftMessage = async (messageId, visibility = 'all', withoutCarrierContacts = false) => {
        setSendingDraftMessageId(messageId);
        try {
            const token = localStorage.getItem('access_token');
            const payload = { message_id: messageId };

            if (isEdnCustomUser) {
                payload.visibility = visibility;
            }
            payload.send_carrier_contacts = !skipDraftCarrierContacts[messageId];
            if (withoutCarrierContacts) {
                payload.confirm_without_carrier_contacts = true;
            }

            const filesForDraft = draftPendingFiles[messageId];
            if (filesForDraft && filesForDraft.length > 0) {
                payload.files = filesForDraft.map(f => ({
                    name: f.name,
                    mime_type: f.mime_type,
                    content: f.content
                }));
            }

            const sendResponse = await axios.post(`/api/messages/send`, payload, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            reportDraftCarrierContacts(sendResponse?.data?.carrier_contacts);

            setDraftPendingFiles(prev => {
                const updated = { ...prev };
                delete updated[messageId];
                return updated;
            });

            fetchMessages();
            // The send has already approved the draft's carrier contacts
            // server-side (KI-108): reload the carrier panel too, or it keeps
            // « Approuver / Rejeter » on a contact already sent (KI-184).
            fetchCarriersConversation();
            if (filesForDraft && filesForDraft.length > 0) {
                fetchTicketFiles();
            }
        } catch (error) {
            const detail = error?.response?.data?.detail;
            if (error?.response?.status === 409 && detail?.code === 'carrier_contact_not_leaving') {
                // Nothing was sent: the operator decides, then the same send again.
                fetchCarriersConversation();
                setNotLeavingConfirm({ messageId, visibility, detail });
                return;
            }
            setOverlayState({
                show: true,
                message: t('messageWindow.sendError', 'Failed to send message'),
                success: false
            });
            hideToastLater();
        } finally {
            setSendingDraftMessageId(null);
        }
    };

    // Outcome of the carrier contacts sent with an approved draft (KI-108).
    const reportDraftCarrierContacts = (outcomes) => {
        if (!Array.isArray(outcomes) || outcomes.length === 0) return;
        const names = (items) => items.map((item) => item.carrier_name || '?').join(', ');
        const sent = outcomes.filter((item) => item.sent);
        const notSent = outcomes.filter((item) => !item.sent);
        const message = notSent.length > 0
            ? t('messageWindow.carrierContactsNotSent', 'Reply sent · carrier contact NOT sent ({{carriers}}): {{error}}', {
                carriers: names(notSent),
                error: notSent.map((item) => (item.error === 'failed_contact'
                    ? t('messageWindow.draftCarrierContactsFailed', 'Carrier contact failed, not sent with this reply — fix it in the carrier panel: {{carriers}}', { carriers: item.carrier_name || '?' })
                    : item.error)).filter(Boolean).join(' · ')
            })
            : t('messageWindow.carrierContactsSent', 'Reply sent · carrier contact sent: {{carriers}}', { carriers: names(sent) });
        setOverlayState({ show: true, message, success: notSent.length === 0 });
        hideToastLater();
    };

    const handleDeleteDraftMessage = async (messageId) => {
        try {
            const token = localStorage.getItem('access_token');
            await axios.post(`/api/messages/delete`, { message_id: messageId }, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            
            // Immediately remove the message from local state for better UX
            setMessages(prevMessages => prevMessages.filter(message => message._id !== messageId));
        } catch (error) {
            // Optionally show an error message to the user
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.deleteError', 'Failed to delete message'), 
                success: false 
            });
            hideToastLater();
        }
    };

    const handleModifyDraftMessage = (message) => {
        setEditingMessage(message);
        setEditedContent(message.content);
    };

    // « 28 oct., 14:05 » — the shared short format (naive UTC, Paris time, KI-110).
    const formatDate = (dateString) => formatShortDateTime(dateString);

    const handleSaveModifiedMessage = async () => {
        try {
            const token = localStorage.getItem('access_token');
            await axios.post(`/api/messages/edit`, {
                message_id: editingMessage._id,
                content: editedContent,
            }, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            setEditingMessage(null);
            setEditedContent('');
            fetchMessages(); // Fetch messages to update the message list
        } catch (error) {
        }
    };

    const handleCancelModification = () => {
        setEditingMessage(null);
        setEditedContent('');
    };

    // File overlay functions
    const handleFileClick = (file) => {
        if (!file) return;
        const targetIndex = attachmentList.findIndex((attachment) => attachment.file === file);
        if (targetIndex !== -1) {
            setSelectedAttachmentIndex(targetIndex);
        } else {
            setSelectedAttachmentIndex(null);
        }
    };

    const closeFileOverlay = () => {
        setSelectedAttachmentIndex(null);
    };

    const handleAttachmentNavigation = (step) => {
        if (selectedAttachmentIndex === null) return;
        const nextIndex = selectedAttachmentIndex + step;
        if (nextIndex < 0 || nextIndex >= attachmentList.length) return;
        setSelectedAttachmentIndex(nextIndex);
    };

    const fetchEmployees = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.get('/api/employees/agents/basic', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setEmployees(response.data);
        } catch (error) {
        }
    };

    const handleAssignTicket = async (employeeId) => {
        if (!ticketId) {
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.assignmentError'), 
                success: false 
            });
            hideToastLater();
            return;
        }

        setAssigningTicket(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post('/api/employees/assign-employee', {
                employee_id: employeeId,
                ticket_id: ticketId
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.assignmentSuccess'), 
                success: true 
            });
            
            // Refresh ticket data to show updated assignment
            await fetchTicketAndMessages();
        } catch (error) {
            setOverlayState({ 
                show: true, 
                message: t('messageWindow.assignmentError'), 
                success: false 
            });
        } finally {
            setAssigningTicket(false);
            hideToastLater();
        }
    };

    useEffect(() => {
        if (!userLoading && !user) {
            navigate('/');
        }
    }, [userLoading, user, navigate]);

    const recipientDetails = contextData?.recipient_info ?? {};
    const clientDetails = contextData?.client_info ?? {};
    const compatibilityDetails = contextData?.compatibility_result;
    const normalizedClientEmail = (clientDetails.email_client || ticket?.customer_email || '')
        .trim()
        .toLowerCase();
    const compatibilitySerialNumber =
        compatibilityDetails?.serial_number ||
        compatibilityDetails?.serialNumber ||
        contextData?.device_info?.serial_number ||
        contextData?.device_info?.serialNumber ||
        null;

    useEffect(() => {
        if (!normalizedClientEmail) {
            latestClientEmailRef.current = '';
            setClientTickets([]);
            setClientTicketsError(null);
            setClientTicketsLoading(false);
            return;
        }

        latestClientEmailRef.current = normalizedClientEmail;
        setClientTicketsLoading(true);
        setClientTicketsError(null);
        fetchClientTickets(normalizedClientEmail);
    }, [normalizedClientEmail, fetchClientTickets, ticketId]);

    useEffect(() => {
        adjustTextareaHeight();
    }, [newMessage, adjustTextareaHeight]);

    useEffect(() => {
        if (selectedAttachmentIndex === null) return;
        if (attachmentList.length === 0) {
            setSelectedAttachmentIndex(null);
            return;
        }
        if (selectedAttachmentIndex > attachmentList.length - 1) {
            setSelectedAttachmentIndex(attachmentList.length - 1);
        }
    }, [attachmentList.length, selectedAttachmentIndex]);



    if (loading) {
        return (
            <div className="tkw-page" aria-busy="true">
                <span className="tkw-sr-only">{t('messageWindow.loading', 'Chargement…')}</span>
                <div className="tkw-header">
                    <div className="tkw-header__main tkw-skeleton-stack">
                        <span className="u-skeleton tkw-skeleton-line tkw-skeleton-line--tiny" />
                        <span className="u-skeleton tkw-skeleton-line tkw-skeleton-line--title" />
                        <span className="u-skeleton tkw-skeleton-line tkw-skeleton-line--short" />
                    </div>
                </div>
                <div className="u-well tkw-well">
                    <div className="tkw-workspace">
                        <div className="u-card tkw-pane tkw-skeleton-pane">
                            <span className="u-skeleton tkw-skeleton-bubble" />
                            <span className="u-skeleton tkw-skeleton-bubble tkw-skeleton-bubble--out" />
                            <span className="u-skeleton tkw-skeleton-bubble" />
                        </div>
                        <div className="u-card tkw-pane tkw-skeleton-pane">
                            <span className="u-skeleton tkw-skeleton-line tkw-skeleton-line--short" />
                            <span className="u-skeleton tkw-skeleton-block" />
                            <span className="u-skeleton tkw-skeleton-block" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!ticket) {
        return (
            <div className="tkw-page tkw-page--empty">
                <div className="u-card tkw-missing">
                    <EmptyState
                        icon={Inbox}
                        title={t('messageWindow.failedToLoadTicket', 'Ce ticket n’a pas pu être chargé')}
                        action={(
                            <Link className="u-btn u-btn--secondary" to="/dashboard">
                                <ArrowLeft strokeWidth={2} aria-hidden="true" />
                                {t('ticketWorkspaceUi.header.backToTickets', 'Retour aux tickets')}
                            </Link>
                        )}
                    >
                        {t('ticketWorkspaceUi.missing', 'Il a peut-être été supprimé, ou il appartient à un autre compte.')}
                    </EmptyState>
                </div>
            </div>
        );
    }


    const isEditable = !TERMINAL_READ_STATUS_VALUES.includes(ticket.status);
    const storedUserData = getStoredUserData();
    const isEdnCustomUser = user?.custom_user === 'edn'
        || user?.user_data?.custom_user === 'edn'
        || storedUserData?.custom_user === 'edn';
    const canDisplayColissimoLabels = Boolean(user) && !isEdnCustomUser;
    const ednStatusOptions = availableEdnStatuses.length > 0 ? availableEdnStatuses : EDN_STATUS_MAPPING;
    const currentEdnStatus = ednStatusOptions.find((statusOption) => statusOption.value === ticket.status);
    const ticketStatusLabel = currentEdnStatus?.name || getStatusLabel(t, ticket.status);
    const currentTopicId = ticket.ticket_topic_id
        || ticket.topic_id
        || ticket.ticket_topic?.id
        || contextData?.shipping_info?.topic_id;
    const currentTopicValue = contextData?.shipping_info?.topic || ticket?.topic;
    const currentTopicLabel = ticket.ticket_topic?.topic_full
        || ticket.ticket_topic?.name
        || ticket.topic_name
        || contextData?.shipping_info?.topic_name
        || currentTopicValue
        || t('messageWindow.noTopic', 'No topic');
    
    const handleLanguageChange = async (language) => {
        setTargetLanguage(language);
        if (language !== 'original') {
            setIsTranslating(true);
            await translateMessages(language);
            setIsTranslating(false);
        } else {
            setTranslatedMessages({});
        }
    };

    const translateMessages = async (language) => {
        try {
            const token = localStorage.getItem('access_token');
            
            // Filter out messages that shouldn't be translated (AI actions, instructions, or messages without content)
            const translatableMessages = messages.filter(m =>
                !m.is_instructions &&
                m.direction !== 'is_action' &&
                m.direction !== 'is_action_result' &&
                m.status !== 'internal' &&
                m.content &&
                m.content.trim() !== ''
            );
            
            if (translatableMessages.length === 0) {
                // No messages to translate
                return;
            }
            
            const response = await axios.post('/api/messages/translate_messages', {
                messages: translatableMessages.map(m => ({ id: m._id, content: m.content })),
                target_language: language
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const translatedObj = {};
            response.data.translated_messages.forEach(m => {
                translatedObj[m.id] = m.content;
            });
            setTranslatedMessages(translatedObj);
        } catch (error) {
            console.error('Translation error:', error);
        }
    };

    const renderMessageContent = (message) => {
        const content = targetLanguage === 'original' ? message.content : (translatedMessages[message._id] || message.content);
        return formatMessageContent(content);
    };

    const handleTextareaChange = (event) => {
        setNewMessage(event.target.value);
        adjustTextareaHeight();
    };

    const handleCopyTracking = () => {
        const tracking = contextData?.shipping_info?.master_tracking;
        if (!tracking || !navigator.clipboard) return;
        navigator.clipboard.writeText(tracking)
            .then(() => {
                setTrackingCopied(true);
                setTimeout(() => setTrackingCopied(false), 2000);
            })
            .catch(() => {});
    };

    // ------------------------------------------------------------------
    // Conversation pieces
    // ------------------------------------------------------------------

    const toggleAiActionExpansion = (key) => {
        setExpandedAiActions(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const parseArguments = (raw) => {
        if (!raw) return null;
        if (typeof raw === 'object') return raw;
        try {
            return JSON.parse(raw);
        } catch (e) {
            return null;
        }
    };

    // The agent's tool calls arrive as an action then its result: one line
    // per tool, the two paired (raw arguments / result behind « Détail »).
    const pairAiSteps = (items) => {
        const steps = [];
        items.forEach((item) => {
            if (item.direction === 'is_action') {
                steps.push({ key: item._id, action: item, result: null });
                return;
            }
            const name = item.function_name;
            const open = [...steps].reverse().find((step) => step.action && !step.result
                && (step.action.function_call?.name || step.action.function_name) === name);
            if (open) {
                open.result = item;
            } else {
                steps.push({ key: item._id, action: null, result: item });
            }
        });
        return steps;
    };

    const stepOutcome = (step) => (step.result ? toolOutcome(step.result.function_result || step.result.content) : null);

    const renderAiStep = (step) => {
        const name = step.action?.function_call?.name || step.action?.function_name || step.result?.function_name || '';
        const args = step.action?.function_call?.arguments ?? step.action?.function_arguments;
        const rawResult = step.result ? (step.result.function_result || step.result.content) : null;
        const outcome = stepOutcome(step);
        const isExpanded = Boolean(expandedAiActions[step.key]);
        const runId = step.action?.run_id || step.result?.run_id;
        const spanId = step.action?.tool_span_id || step.action?.span_id || step.result?.tool_span_id || step.result?.span_id;
        let assignment = null;
        if (name === 'assign_to_employee') {
            const parsed = parseArguments(args);
            if (parsed?.summary) assignment = { summary: parsed.summary, email: parsed.email || '' };
        }
        const tone = outcome ? outcome.kind : 'none';
        const StatusIcon = tone === 'ok' ? CircleCheck : tone === 'error' ? CircleX : CircleDashed;
        const statusText = !outcome
            ? t('ticketWorkspaceUi.thread.stepNoResult', 'Sans résultat enregistré')
            : outcome.kind === 'ok'
                ? t('messageWindow.aiActionDone', 'Terminé')
                : outcome.kind === 'error'
                    ? t('messageWindow.aiActionFailed', 'Échec')
                    : outcome.status;
        const detailLabel = isExpanded
            ? t('ticketWorkspaceUi.thread.hideStepDetail', 'Masquer le détail')
            : t('ticketWorkspaceUi.thread.showStepDetail', 'Voir le détail');
        return (
            <li key={step.key} className={`ai-action tkw-step tkw-step--${tone}${isExpanded ? ' is-open' : ''}`}>
                <div className="tkw-step__row">
                    <StatusIcon className="tkw-step__icon" strokeWidth={2} aria-hidden="true" />
                    <span className="tkw-step__label">{toolLabel(name, t)}</span>
                    <span className="tkw-step__status">{statusText}</span>
                    <span className="tkw-step__actions">
                        {runId && (
                            <button
                                type="button"
                                className="u-btn u-btn--ghost u-btn--sm tkw-step__run"
                                onClick={() => openAiRun(runId, spanId)}
                                title={t('messageWindow.viewAiRunTitle', 'Voir la trace IA qui a produit ce message')}
                            >
                                <Workflow strokeWidth={1.8} aria-hidden="true" />
                                {t('messageWindow.viewAiRun', 'Run IA')}
                            </button>
                        )}
                        <button
                            type="button"
                            className="u-btn u-btn--ghost u-btn--icon u-btn--sm tkw-step__toggle"
                            onClick={() => toggleAiActionExpansion(step.key)}
                            aria-expanded={isExpanded}
                            aria-label={detailLabel}
                            title={detailLabel}
                        >
                            <ChevronDown strokeWidth={2} />
                        </button>
                    </span>
                </div>
                {assignment && (
                    <div className="tkw-step__note">
                        <UserPlus strokeWidth={1.8} aria-hidden="true" />
                        <span>
                            <strong>{t('messageWindow.assignedTo', 'Assigné à')} {assignment.email.split('@')[0]}</strong>
                            {' · '}{assignment.summary}
                        </span>
                    </div>
                )}
                {isExpanded && (
                    <div className="tkw-step__detail">
                        <div className="tkw-step__detail-label">
                            {t('ticketWorkspaceUi.thread.toolName', 'Outil')} <code className="u-code">{name}</code>
                        </div>
                        {args && (
                            <>
                                <div className="tkw-step__detail-label">{t('messageWindow.arguments', 'Arguments')}</div>
                                <pre className="tkw-json">{prettyJson(args)}</pre>
                            </>
                        )}
                        {rawResult && (
                            <>
                                <div className="tkw-step__detail-label">{t('messageWindow.result', 'Résultat')}</div>
                                <pre className="tkw-json">{prettyJson(rawResult)}</pre>
                            </>
                        )}
                    </div>
                )}
            </li>
        );
    };

    const renderAiSteps = (entry) => {
        const steps = pairAiSteps(entry.items);
        const failed = steps.filter((step) => stepOutcome(step)?.kind === 'error').length;
        return (
            <section key={entry.key} className="tkw-steps" aria-label={t('ticketWorkspaceUi.thread.aiSteps', 'Actions de l’IA')}>
                <div className="tkw-steps__head">
                    <Sparkles strokeWidth={1.8} aria-hidden="true" />
                    <span>{t('ticketWorkspaceUi.thread.aiSteps', 'Actions de l’IA')}</span>
                    <span className="tkw-steps__count">{steps.length}</span>
                    {failed > 0 && (
                        <Badge tone="danger">{t('ticketWorkspaceUi.thread.stepsFailed', 'Échecs : {{count}}', { count: failed })}</Badge>
                    )}
                </div>
                <ul className="tkw-steps__list">
                    {steps.map(renderAiStep)}
                </ul>
            </section>
        );
    };

    // Toggle note expansion for a specific message
    const toggleNoteExpansion = (messageId) => {
        setExpandedNotes(prev => ({
            ...prev,
            [messageId]: !(prev[messageId] ?? true)
        }));
    };

    // A message's notes, open by default and aligned with their message (an
    // AI draft or a sent reply on the right, a customer message on the left).
    const renderNoteCard = (message) => {
        if (!message.notes || message.notes.length === 0) return null;

        const isExpanded = expandedNotes[message._id] ?? true;
        const noteCount = message.notes.length;
        const isOutgoing = message.sender === 'employee' || ['sent', 'draft', 'superseded'].includes(message.status);

        return (
            <div key={`notes-${message._id}`}
                className={`note-card tkw-note ${isOutgoing ? 'note-card--outgoing' : 'note-card--incoming'} ${isExpanded ? 'expanded' : 'collapsed'}`}>
                <button
                    type="button"
                    className="note-card-header tkw-note__head"
                    onClick={() => toggleNoteExpansion(message._id)}
                    aria-expanded={isExpanded}
                >
                    <StickyNote strokeWidth={1.8} aria-hidden="true" />
                    <span className="note-card-title">{t('messageWindow.notes', 'Notes')}</span>
                    <span className="note-card-count">{noteCount}</span>
                    <ChevronDown className="tkw-note__chevron" strokeWidth={2} aria-hidden="true" />
                </button>
                <div className={`note-card-content ${isExpanded ? 'visible' : ''}`}>
                    {message.notes.map((note, index) => (
                        <div key={index} className="note-item">
                            <div className="note-content"><LinkifiedText text={note.content} /></div>
                            <div className="note-meta">
                                {note.added_by && <span className="note-author">{note.added_by}</span>}
                                <span className="note-date">{formatNoteDate(note.added_at)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderMessageAttachments = (messageId) => {
        const filesForMessage = messageFiles[messageId];
        if (!filesForMessage || filesForMessage.length === 0) {
            return null;
        }

        const decorativeFiles = filesForMessage.filter((file) => isDecorativeFile(file));
        const showDecorative = Boolean(expandedDecorative[messageId]);
        const visibleFiles = filesForMessage.filter((file) => showDecorative || !isDecorativeFile(file));

        return (
            <div className="message-attachments tkw-files">
                {visibleFiles.map((file, index) => (
                    <button
                        type="button"
                        key={`${messageId}-${file?.metadata?.file_id || file?.name || index}`}
                        className={`message-attachment tkw-file tkw-file--${fileKind(file?.mime_type || '')}${isDecorativeFile(file) ? ' message-attachment--decorative' : ''}`}
                        onClick={() => handleFileClick(file)}
                        title={attachmentTooltip(file)}
                    >
                        <FileGlyph mimeType={file?.mime_type || ''} />
                        <span className="tkw-file__name">
                            {file?.reading?.title || file?.name || t('messageWindow.attachment', 'Pièce jointe')}
                        </span>
                        {file?.reading && <Sparkles className="tkw-file__read" strokeWidth={1.8} aria-label={t('messageWindow.readByAi', "Lu par l'IA")} />}
                    </button>
                ))}
                {decorativeFiles.length > 0 && (
                    <button
                        type="button"
                        className="message-attachment message-attachment--decorative-toggle tkw-file tkw-file--toggle"
                        onClick={() => setExpandedDecorative((prev) => ({ ...prev, [messageId]: !prev[messageId] }))}
                        title={t('messageWindow.decorativeFileHint', 'Signature, logo or icon of an e-mail: never read by the AI nor sent to a carrier.')}
                        aria-expanded={showDecorative}
                    >
                        <span className="tkw-file__name">
                            {t('messageWindow.decorativeFiles', 'Signature images ({{count}})', { count: decorativeFiles.length })}
                        </span>
                    </button>
                )}
            </div>
        );
    };

    // KI-229: an e-mail shows what its sender wrote; the earlier mails their
    // client quoted below it (``quoted_history``) stay one click away.
    const renderQuotedHistory = (message) => {
        const quoted = typeof message.quoted_history === 'string' ? message.quoted_history.trim() : '';
        if (!quoted) {
            return null;
        }
        const isOpen = Boolean(expandedQuotedHistory[message._id]);
        return (
            <div className="message-quoted-history tkw-quoted">
                <button
                    type="button"
                    className="message-quoted-history-toggle tkw-quoted__toggle"
                    onClick={() => setExpandedQuotedHistory((prev) => ({ ...prev, [message._id]: !prev[message._id] }))}
                    title={t('messageWindow.quotedHistoryHint', "Earlier e-mails the sender's mail client copied below this message")}
                    aria-expanded={isOpen}
                >
                    <ChevronDown className="tkw-quoted__chevron" strokeWidth={2} aria-hidden="true" />
                    <span>
                        {isOpen
                            ? t('messageWindow.hideQuotedHistory', 'Hide history')
                            : t('messageWindow.showQuotedHistory', 'Show history')}
                    </span>
                </button>
                {isOpen && (
                    <div className="message-quoted-history-content tkw-quoted__content">
                        <LinkifiedText text={quoted} />
                    </div>
                )}
            </div>
        );
    };

    const toggleHandoffMessage = (messageId) => {
        setExpandedHandoffMessages((prev) => ({
            ...prev,
            [messageId]: !prev[messageId],
        }));
    };

    // Who wrote a message, in one short line (the customer's address, the AI
    // agent, the team member). The team writing outside Unanimity has its own
    // origin line instead.
    const messageAuthor = (message, isSent) => {
        if (message.origin_kind === 'team_outside') return null;
        if (!isSent) {
            return senderAddress(message) || ticket.customer_email || t('ticketWorkspaceUi.thread.customer', 'Client');
        }
        return message.agent_name || message.sent_by_name || null;
    };

    const renderDraftFooter = (message) => {
        const held = Array.isArray(message.held_carrier_contacts) ? message.held_carrier_contacts : [];
        const toSend = held.filter((contact) => !contact.failed);
        const failed = held.filter((contact) => contact.failed);
        const names = (items) => items.map((contact) => contact.carrier_name || '?').join(', ');
        const isSending = sendingDraftMessageId === message._id;
        return (
            <div className="tkw-draft">
                {message.metadata?.send_policy?.reason === 'send_failed' && (
                    <div className="draft-send-failed tkw-draft__alert" role="status">
                        <CircleX strokeWidth={2} aria-hidden="true" />
                        <span>{t('messageWindow.draftSendFailed', 'Automatic sending failed: this reply was not delivered. Check it, then send it.')}</span>
                    </div>
                )}
                {draftPendingFiles[message._id] && draftPendingFiles[message._id].length > 0 && (
                    <div className="tkw-files">
                        {draftPendingFiles[message._id].map((file, index) => (
                            <span key={index} className="tkw-file tkw-file--pending">
                                <FileGlyph mimeType={file.mime_type || ''} />
                                <span className="tkw-file__name">{file.name}</span>
                                <button
                                    type="button"
                                    className="tkw-file__remove"
                                    onClick={() => handleRemoveDraftPendingFile(message._id, index)}
                                    title={t('messageWindow.removeFile', 'Remove file')}
                                    aria-label={t('messageWindow.removeFile', 'Remove file')}
                                >
                                    <X strokeWidth={2} />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
                {held.length > 0 && (
                    <div className="draft-carrier-contacts tkw-draft__carrier">
                        {toSend.length > 0 && (
                            <label title={t('messageWindow.draftCarrierContactsHint', 'The AI prepared this contact in the same run as the reply. Untick to keep it for a separate validation in the carrier panel.')}>
                                <input
                                    type="checkbox"
                                    checked={!skipDraftCarrierContacts[message._id]}
                                    disabled={isSending}
                                    onChange={(event) => setSkipDraftCarrierContacts((prev) => ({ ...prev, [message._id]: !event.target.checked }))}
                                />
                                <Truck strokeWidth={1.8} aria-hidden="true" />
                                <span>{t('messageWindow.draftCarrierContacts', 'Also send the carrier contact prepared with this reply: {{carriers}}', { carriers: names(toSend) })}</span>
                            </label>
                        )}
                        {failed.length > 0 && (
                            <span className="draft-carrier-contacts-failed tkw-draft__failed">
                                {t('messageWindow.draftCarrierContactsFailed', 'Carrier contact failed, not sent with this reply — fix it in the carrier panel: {{carriers}}', { carriers: names(failed) })}
                            </span>
                        )}
                    </div>
                )}
                <div className="draft-actions tkw-draft__actions">
                    <span className="tkw-draft__hint">{t('ticketWorkspaceUi.thread.draftHint', 'Rien n’est envoyé au client sans votre validation.')}</span>
                    <OverflowMenu
                        label={t('ticketWorkspaceUi.thread.draftMore', 'Autres actions sur le brouillon')}
                        items={[
                            {
                                label: t('messageWindow.attach', 'Attach'),
                                icon: Paperclip,
                                onClick: () => handleDraftFileAttachClick(message._id),
                                disabled: isSending,
                            },
                            { separator: true },
                            {
                                label: t('ticketWorkspaceUi.thread.deleteDraft', 'Supprimer le brouillon…'),
                                icon: Trash2,
                                danger: true,
                                onClick: () => setDraftToDelete(message._id),
                            },
                        ]}
                    />
                    <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => handleModifyDraftMessage(message)}>
                        <Pencil strokeWidth={1.8} aria-hidden="true" />
                        {t('messageWindow.modify', 'Modifier')}
                    </button>
                    <button
                        type="button"
                        className="u-btn u-btn--primary u-btn--sm"
                        data-tour="draft-approve"
                        onClick={() => handleSendDraftMessage(message._id)}
                        disabled={isSending}
                    >
                        {isSending
                            ? <LoaderCircle className="tkw-spin" strokeWidth={2} aria-hidden="true" />
                            : <Send strokeWidth={1.8} aria-hidden="true" />}
                        {isSending ? t('messageWindow.sending') : t('messageWindow.send')}
                    </button>
                </div>
            </div>
        );
    };

    const renderMessage = (message) => {
        // Internal AI activity record: actions taken by the AI without replying
        // to the customer (e.g. assignment before pass_response). Never sent.
        if (message.status === 'internal') {
            return (
                <div key={message._id} className="message-internal-activity tkw-event">
                    <IconChip icon={Info} tone="neutral" size="sm" />
                    <div className="tkw-event__body">
                        <div className="tkw-event__title">{t('messageWindow.aiInternalActivity', 'AI action (no reply sent)')}</div>
                        <div className="internal-activity-content tkw-event__text">
                            {formatMessageContent(message.content || '')}
                        </div>
                    </div>
                    <span className="tkw-event__time">{formatShortDateTime(message.created_at)}</span>
                </div>
            );
        }

        // The instructions a person gave the AI when handing the ticket over.
        if (message.is_instructions) {
            const isExpanded = !!expandedHandoffMessages[message._id];
            return (
                <div key={message._id} className={`message-instruction tkw-event tkw-event--ai ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    <IconChip icon={Sparkles} size="sm" />
                    <div className="tkw-event__body">
                        <div className="tkw-event__title">{t('ticketWorkspaceUi.thread.instructions', 'Consignes données à l’IA')}</div>
                        <div className={`instruction-content tkw-event__text ${isExpanded ? 'instruction-content-expanded' : ''}`}>
                            {isExpanded ? (
                                renderMessageContent(message)
                            ) : (
                                <span className="instruction-preview">
                                    {getHandoffPreview(message.content)}
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            className="instruction-toggle-button tkw-event__toggle"
                            onClick={() => toggleHandoffMessage(message._id)}
                            aria-expanded={isExpanded}
                        >
                            {isExpanded
                                ? t('messageWindow.hideHandoffDetails', 'Hide full instructions')
                                : t('messageWindow.showHandoffDetails', 'Show full instructions')}
                        </button>
                    </div>
                    <span className="tkw-event__time">{formatShortDateTime(message.created_at)}</span>
                </div>
            );
        }

        // Regular message rendering
        const isSent = message.sender === 'employee' || message.status === 'sent' || message.status === 'draft' || message.status === 'superseded';
        const ednMessageVisibility = (message.metadata?.visibility || '').toLowerCase();
        const shouldShowEdnVisibilityBadge = isEdnCustomUser
            && isSent
            && ['all', 'admin'].includes(ednMessageVisibility);
        // The team wrote it outside Unanimity (Outlook, an external ticketing
        // system, a provider's tool): coloured apart from what was sent here.
        const isTeamOutside = message.origin_kind === 'team_outside';
        const isDraft = message.status === 'draft';
        const isAiAuthored = message.origin_kind === 'ai' || (!message.origin_kind && isSent && Boolean(message.agent_name));
        const messageClass = [
            'message',
            isSent ? 'sent' : 'received',
            message.status,
            shouldShowEdnVisibilityBadge && 'with-edn-visibility',
            isTeamOutside && 'origin-team-outside',
            'tkw-msg',
            isSent ? 'tkw-msg--out' : 'tkw-msg--in',
            isDraft && 'tkw-msg--draft',
            message.status === 'superseded' && 'tkw-msg--superseded',
            isAiAuthored && 'tkw-msg--ai',
        ].filter(Boolean).join(' ');

        const isBeingEdited = editingMessage && editingMessage._id === message._id;
        const author = messageAuthor(message, isSent);
        const sentByName = message.sent_by_name || null;
        const hasNotes = message.notes && message.notes.length > 0;
        const statusLabel = message.status
            ? t(`messageWindow.statusOptions.${message.status}`, message.status)
            : t('messageWindow.statusOptions.received', 'Reçu');

        return (
            <article key={message._id} className={messageClass} data-message-status={message.status} data-message-id={message._id}>
                <header className="tkw-msg__head">
                    {isTeamOutside ? (
                        <div className="message-origin-label tkw-msg__origin">
                            <ArrowRight className="tkw-msg__origin-icon" strokeWidth={2} aria-hidden="true" />
                            <span>
                                {t('messageWindow.origin.outside', 'Sent outside Unanimity · {{source}}', { source: outsideSourceLabel(t, message.outside_source) })}
                            </span>
                            {outsideAuthor(message) && (
                                <span className="message-origin-author">{outsideAuthor(message)}</span>
                            )}
                        </div>
                    ) : (
                        <span className="tkw-msg__author">
                            {isAiAuthored && <Sparkles className="tkw-msg__ai-icon" strokeWidth={1.8} aria-hidden="true" />}
                            {author && <span className="message-agent-name-primary">{author}</span>}
                            {isSent && sentByName && sentByName !== author && (
                                <span className="message-sent-by-name">
                                    {t('messageWindow.sentByLabel', 'Sent by')} {sentByName}
                                </span>
                            )}
                        </span>
                    )}
                    <span className="tkw-msg__badges">
                        {isDraft && (
                            <Badge tone="warning" dot>{t('ticketWorkspaceUi.thread.draftBadge', 'Brouillon de l’IA')}</Badge>
                        )}
                        {message.status === 'superseded' && <Badge>{statusLabel}</Badge>}
                        {message.status === 'automated' && (
                            <Badge tone="accent">{t('messageWindow.automated')}</Badge>
                        )}
                        {shouldShowEdnVisibilityBadge && (
                            <span className={`edn-message-visibility-badge u-badge ${ednMessageVisibility === 'admin' ? 'u-badge--warning' : 'u-badge--info'}`}>
                                {ednMessageVisibility === 'admin'
                                    ? t('messageWindow.visibilityAdminShort', 'Admin')
                                    : t('messageWindow.visibilityAllShort', 'All')}
                            </span>
                        )}
                    </span>
                </header>

                {isBeingEdited ? (
                    <div className="message-edit-container tkw-msg__edit">
                        <textarea
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            className="u-textarea message-edit-textarea"
                            rows={6}
                            placeholder={t('messageWindow.editMessage')}
                            aria-label={t('messageWindow.editMessage', 'Modifier le message')}
                        />
                        <div className="edit-actions tkw-msg__edit-actions">
                            <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={handleCancelModification}>
                                {t('messageWindow.cancel')}
                            </button>
                            <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={handleSaveModifiedMessage}>
                                <Check strokeWidth={2} aria-hidden="true" />
                                {t('messageWindow.save')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="message-content tkw-msg__body">
                            {renderMessageContent(message)}
                        </div>
                        {renderQuotedHistory(message)}
                    </>
                )}

                {renderMessageAttachments(message._id)}

                <footer className="message-meta tkw-msg__foot">
                    <span className="tkw-msg__meta">
                        {!isDraft && message.status !== 'superseded' && <span className="message-status">{statusLabel}</span>}
                        <span className="tkw-msg__time" title={formatDate(message.created_at)}>
                            {message.sent_at && message.sent_at !== message.created_at
                                ? t('ticketWorkspaceUi.thread.sentAt', 'envoyé le {{date}}', { date: formatShortDateTime(message.sent_at) })
                                : formatShortDateTime(message.created_at)}
                        </span>
                    </span>
                    <span className="message-actions tkw-msg__tools">
                        {user && user.role !== 'experience' && (
                            <button
                                type="button"
                                className={`note-button u-btn u-btn--ghost u-btn--sm ${hasNotes ? 'has-notes' : ''}`}
                                onClick={() => openNoteModal(message)}
                                title={t('messageWindow.addNote')}
                                aria-label={t('messageWindow.addNote')}
                            >
                                <StickyNote strokeWidth={1.8} aria-hidden="true" />
                                {hasNotes && <span className="note-count">{message.notes.length}</span>}
                            </button>
                        )}
                        {message.run_id && (
                            <button
                                type="button"
                                className="run-link-button u-btn u-btn--ghost u-btn--sm"
                                onClick={() => openAiRun(message.run_id, message.span_id)}
                                title={t('messageWindow.viewAiRunTitle', 'Voir la trace IA qui a produit ce message')}
                            >
                                <Workflow strokeWidth={1.8} aria-hidden="true" />
                                <span>{t('messageWindow.viewAiRun', 'Run IA')}</span>
                            </button>
                        )}
                    </span>
                </footer>

                {isDraft && isEditable && !isBeingEdited && renderDraftFooter(message)}
            </article>
        );
    };

    const handleValidateCarrierMessage = async (conversationId, approved) => {
        setValidatingMessage(true);
        try {
            const token = localStorage.getItem('access_token');
            const validation = await axios.post('/api/logistics/validate-carrier-message', 
                {
                    conversation_id: conversationId,
                    approved: approved
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            // Form / API carriers (FedEx, DHL, GLS) answer the outcome in the
            // body: a refused or failed send is {success: false, error}. It
            // used to be ignored — the buttons re-enabled and nothing said why.
            if (validation?.data?.success === false) {
                await fetchCarriersConversation();
                setOverlayState({
                    show: true,
                    message: t(
                        'messageWindow.carrierContactFailed',
                        'Carrier contact failed: {{error}}',
                        { error: validation.data.error || t('messageWindow.validationError') }
                    ),
                    success: false
                });
                return;
            }

            // Approval queues an async portal task. Poll until the message
            // leaves "processing" so a fast failure surfaces instead of
            // leaving the UI stuck on "Envoi en cours".
            let latestConversation = null;
            for (let attempt = 0; attempt < 8; attempt += 1) {
                await new Promise((resolve) => setTimeout(resolve, attempt === 0 ? 800 : 1500));
                const response = await axios.get(
                    `/api/tickets/carriers_conversation/${ticketId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                latestConversation = response.data;
                setCarriersConversation(latestConversation);
                const stillProcessing =
                    latestConversation?.status === 'processing' ||
                    (latestConversation?.messages || []).some((msg) => msg.status === 'processing');
                if (!stillProcessing) {
                    break;
                }
            }

            if (approved && latestConversation?.error) {
                setOverlayState({
                    show: true,
                    message: t(
                        'messageWindow.carrierContactFailed',
                        'Carrier contact failed: {{error}}',
                        { error: latestConversation.error }
                    ),
                    success: false
                });
            } else {
                setOverlayState({ 
                    show: true, 
                    message: approved ? t('messageWindow.messageApproved') : t('messageWindow.messageRejected'), 
                    success: true 
                });
            }
        } catch (error) {
            // A 4xx from the backend carries the operator's instruction
            // (e.g. no recipient address configured for the carrier).
            setOverlayState({ 
                show: true, 
                message: error.response?.data?.detail || t('messageWindow.validationError'), 
                success: false 
            });
        } finally {
            setValidatingMessage(false);
            hideToastLater();
        }
    };

    // What the AI agent read in a file (read_ticket_file): title, description,
    // date. Shown on the attachment chips, the attachments list and the viewer.
    const readingDate = (reading) => formatShortDateTime(reading?.read_at);

    const attachmentTooltip = (file) => {
        if (isDecorativeFile(file)) {
            return [file?.name, t('messageWindow.decorativeFileHint', 'Signature, logo or icon of an e-mail: never read by the AI nor sent to a carrier.'),
                file?.metadata?.decorative_reason].filter(Boolean).join('\n\n');
        }
        if (!file?.reading) return file?.name || '';
        return [file.reading.title, file.reading.description, file.name].filter(Boolean).join('\n\n');
    };


    // Add file processing function
    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Remove the data:image/jpeg;base64, prefix
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = error => reject(error);
        });
    };

    // Handle file selection - store files in state to be sent with message
    const handleFileSelection = async (event) => {
        const files = Array.from(event.target.files);
        
        if (files.length === 0) return;
        
        // Validate file count (max 10 files total including already pending)
        if (pendingFiles.length + files.length > 10) {
            setOverlayState({
                show: true,
                message: t('messageWindow.maxFilesError'),
                success: false
            });
            hideToastLater();
            return;
        }

        // Validate total file size (max 50MB total)
        const existingSize = pendingFiles.reduce((sum, f) => sum + f.size, 0);
        const newSize = files.reduce((sum, file) => sum + file.size, 0);
        const maxSize = 50 * 1024 * 1024; // 50MB
        
        if (existingSize + newSize > maxSize) {
            setOverlayState({
                show: true,
                message: t('messageWindow.fileSizeError'),
                success: false
            });
            hideToastLater();
            return;
        }

        setUploadingFiles(true);
        
        try {
            const processedFiles = [];

            // Process all files to base64
            for (const file of files) {
                const base64Content = await convertFileToBase64(file);
                processedFiles.push({
                    name: file.name,
                    mime_type: file.type,
                    content: base64Content,
                    size: file.size
                });
            }

            // Add to pending files instead of uploading
            setPendingFiles(prev => [...prev, ...processedFiles]);
            
            // Clear file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.fileProcessError', 'Error processing files'),
                success: false
            });
            hideToastLater();
        } finally {
            setUploadingFiles(false);
        }
    };

    // Function to remove a pending file
    const handleRemovePendingFile = (index) => {
        setPendingFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleDraftFileAttachClick = (messageId) => {
        setActiveDraftForFileUpload(messageId);
        setTimeout(() => draftFileInputRef.current?.click(), 0);
    };

    const handleDraftFileSelection = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0 || !activeDraftForFileUpload) return;

        const messageId = activeDraftForFileUpload;
        const existing = draftPendingFiles[messageId] || [];

        if (existing.length + files.length > 10) {
            setOverlayState({ show: true, message: t('messageWindow.maxFilesError'), success: false });
            hideToastLater();
            if (draftFileInputRef.current) draftFileInputRef.current.value = '';
            return;
        }

        const existingSize = existing.reduce((sum, f) => sum + f.size, 0);
        const newSize = files.reduce((sum, file) => sum + file.size, 0);
        if (existingSize + newSize > 50 * 1024 * 1024) {
            setOverlayState({ show: true, message: t('messageWindow.fileSizeError'), success: false });
            hideToastLater();
            if (draftFileInputRef.current) draftFileInputRef.current.value = '';
            return;
        }

        try {
            const processedFiles = [];
            for (const file of files) {
                const base64Content = await convertFileToBase64(file);
                processedFiles.push({
                    name: file.name,
                    mime_type: file.type,
                    content: base64Content,
                    size: file.size
                });
            }
            setDraftPendingFiles(prev => ({
                ...prev,
                [messageId]: [...(prev[messageId] || []), ...processedFiles]
            }));
        } catch (error) {
            setOverlayState({ show: true, message: t('messageWindow.fileProcessError', 'Error processing files'), success: false });
            hideToastLater();
        } finally {
            if (draftFileInputRef.current) draftFileInputRef.current.value = '';
            setActiveDraftForFileUpload(null);
        }
    };

    const handleRemoveDraftPendingFile = (messageId, index) => {
        setDraftPendingFiles(prev => {
            const updated = { ...prev };
            updated[messageId] = (updated[messageId] || []).filter((_, i) => i !== index);
            if (updated[messageId].length === 0) delete updated[messageId];
            return updated;
        });
    };

    // Add API function for adding notes
    const handleAddNote = async () => {
        if (!noteContent.trim() || !selectedMessageForNote) return;

        setAddingNote(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post('/api/messages/add_note', {
                message_id: selectedMessageForNote._id,
                note: noteContent.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Refresh messages to show the new note
            await fetchMessages();
            
            // Reset note form
            setNoteContent('');
            setSelectedMessageForNote(null);
            setShowNoteModal(false);
            
            setOverlayState({
                show: true,
                message: t('messageWindow.noteAddedSuccess'),
                success: true
            });
        } catch (error) {
            setOverlayState({
                show: true,
                message: t('messageWindow.noteAddError'),
                success: false
            });
        } finally {
            setAddingNote(false);
            hideToastLater();
        }
    };

    // Function to open note modal
    const openNoteModal = (message) => {
        setSelectedMessageForNote(message);
        setNoteContent('');
        setShowNoteModal(true);
    };

    // Function to close note modal
    const closeNoteModal = () => {
        setShowNoteModal(false);
        setNoteContent('');
        setSelectedMessageForNote(null);
    };

    // Function to format note date
    // « 28 oct., 14:05 » — the shared short format (naive UTC, Paris time, KI-110).
    const formatNoteDate = (dateString) => formatShortDateTime(dateString);

    // Add function to handle AI handoff
    const handleAIHandoff = async () => {
        if (!handoffInstructions.trim() || !user) return;

        setHandingOffToAI(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await axios.post(`/api/tickets/${ticketId}/agent-handoff`, {
                instructions: handoffInstructions.trim(),
                user_id: user._id
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const successMessage = t('messageWindow.handoffSuccess', {
                defaultValue: 'Ticket successfully handed off to AI',
                automation: response.data.automation_enabled ? 
                    t('messageWindow.messageSent', 'Message sent') : 
                    t('messageWindow.savedAsDraft', 'Saved as draft')
            });
            
            redirectWithNotification(successMessage, true);
        } catch (error) {
            const errorMessage = error.response?.data?.detail || t('messageWindow.handoffError', 'Failed to hand off ticket to AI');
            redirectWithNotification(errorMessage, false);
        } finally {
            setHandingOffToAI(false);
        }
    };

    // Add function to open handoff modal
    const openHandoffModal = () => {
        setHandoffInstructions('');
        setShowHandoffModal(true);
    };

    // Add function to close handoff modal
    const closeHandoffModal = () => {
        setShowHandoffModal(false);
        setHandoffInstructions('');
    };

    // Add function to handle Slack message sending
    const handleSendSlackMessage = async () => {
        if (!slackMessage.trim() || !ticketId) return;

        setSendingSlackMessage(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post('/api/send-slack-message', {
                ticket_id: ticketId,
                message: slackMessage.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            // Reset form and close modal
            setSlackMessage('');
            setShowSlackModal(false);
            
            setOverlayState({
                show: true,
                message: t('messageWindow.slackMessageSentSuccess', 'Message sent to support team successfully'),
                success: true
            });
        } catch (error) {
            setOverlayState({
                show: true,
                message: error.response?.data?.detail || t('messageWindow.slackMessageSendError', 'Failed to send message to support team'),
                success: false
            });
        } finally {
            setSendingSlackMessage(false);
            hideToastLater();
        }
    };

    // Add function to open Slack modal
    const openSlackModal = () => {
        setSlackMessage('');
        setShowSlackModal(true);
    };

    // Add function to close Slack modal
    const closeSlackModal = () => {
        setShowSlackModal(false);
        setSlackMessage('');
    };

    // Functions for bug report
    const openBugReportModal = () => {
        setBugDescription('');
        setShowBugReportModal(true);
    };

    const closeBugReportModal = () => {
        if (sendingBugReport) return;
        setShowBugReportModal(false);
        setBugDescription('');
    };

    const handleSendBugReport = async () => {
        if (!bugDescription.trim() || !ticketId) return;

        setSendingBugReport(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post(`/api/tickets/${ticketId}/report-bug`, {
                description: bugDescription.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setBugDescription('');
            setShowBugReportModal(false);

            setOverlayState({
                show: true,
                message: t('messageWindow.bugReportSentSuccess', 'Bug report sent to the support team'),
                success: true
            });
        } catch (error) {
            const detail = error.response?.data?.detail;
            const message = typeof detail === 'object' && detail !== null
                ? (detail.message || t('messageWindow.bugReportSendError', 'Failed to send the bug report'))
                : (detail || t('messageWindow.bugReportSendError', 'Failed to send the bug report'));
            setOverlayState({
                show: true,
                message,
                success: false
            });
        } finally {
            setSendingBugReport(false);
            hideToastLater();
        }
    };

    // Functions for email forwarding
    const parseEmailList = (input) => {
        return input
            .split(/[,;\s]+/)
            .map((email) => email.trim())
            .filter((email) => email.length > 0);
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const forwardToEmails = parseEmailList(forwardToInput);
    const forwardCcEmails = parseEmailList(forwardCcInput);
    const forwardEmailsValid =
        forwardToEmails.length > 0 &&
        forwardToEmails.every(isValidEmail) &&
        forwardCcEmails.every(isValidEmail);

    const openForwardModal = () => {
        setForwardToInput('');
        setForwardCcInput('');
        setForwardComment('');
        setShowForwardModal(true);
    };

    const closeForwardModal = () => {
        if (forwardingEmail) return;
        setShowForwardModal(false);
        setForwardToInput('');
        setForwardCcInput('');
        setForwardComment('');
    };

    const handleForwardEmail = async () => {
        if (!forwardEmailsValid || !ticketId || forwardingEmail) return;

        setForwardingEmail(true);
        try {
            const token = localStorage.getItem('access_token');
            await axios.post('/api/messages/forward', {
                ticket_id: ticketId,
                forward_to_emails: forwardToEmails,
                cc_emails: forwardCcEmails.length > 0 ? forwardCcEmails : null,
                comment: forwardComment.trim() || null
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setShowForwardModal(false);
            setForwardToInput('');
            setForwardCcInput('');
            setForwardComment('');

            setOverlayState({
                show: true,
                message: t('messageWindow.forwardEmailSuccess', 'Email forwarded successfully'),
                success: true
            });

            fetchMessages();
        } catch (error) {
            setOverlayState({
                show: true,
                message: error.response?.data?.detail || t('messageWindow.forwardEmailError', 'Failed to forward the email'),
                success: false
            });
        } finally {
            setForwardingEmail(false);
            hideToastLater();
        }
    };

    const displayedMessages = messages.filter((message) => {
        const isDraftLike = message.status === 'draft' || message.status === 'superseded';
        if (hideDraftMessages && isDraftLike) {
            return false;
        }

        const messageText = normalizeMessageContent(message.content).trim();
        const hasPersistedFiles = (messageFiles[message._id] || []).length > 0;
        const hasPendingDraftFiles = (draftPendingFiles[message._id] || []).length > 0;

        return !isDraftLike || messageText || hasPersistedFiles || hasPendingDraftFiles;
    });

    // ------------------------------------------------------------------
    // What the page knows, derived once for the zones below
    // ------------------------------------------------------------------
    const canAct = Boolean(user) && user.role !== 'experience';
    const shippingInfo = contextData?.shipping_info || {};
    const orderInfo = contextData?.order_info || {};
    const trackingNumber = shippingInfo.master_tracking || '';
    const orderReference = orderInfo.reference || orderInfo.order_number || '';
    const externalTicketId = shippingInfo.external_ticket_id || ticket.external_ticket_id || '';
    const assignedName = employee && employee !== 'N/A' && String(employee).toLowerCase() !== 'not assigned' ? employee : '';
    const ChannelIcon = channelIcons[ticket.channel_name] || Mail;
    const statusTone = ticketStatusTone(ticket.status);
    const dashboardSearch = (() => {
        try {
            return location.state?.fromDashboardSearch || sessionStorage.getItem('una_dashboard_search') || '';
        } catch (error) {
            return '';
        }
    })();

    const carrierConversationExists = Boolean(carriersConversation && typeof carriersConversation === 'object'
        && !Array.isArray(carriersConversation)
        && (carriersConversation._id || (carriersConversation.messages || []).length > 0));
    const carrierMessages = carrierConversationExists && Array.isArray(carriersConversation.messages) ? carriersConversation.messages : [];
    const carrierAwaits = carrierConversationExists
        && CARRIER_AWAITING_STATUSES.includes(carriersConversation.status)
        && (carrierMessages.length === 0 || carrierMessages.some((m) => (m.status === 'pending' || !m.status)
            && m.direction !== 'received' && m.direction !== 'final_response'));
    const carrierRetry = carrierConversationExists ? carriersConversation.retry_state : null;
    const carrierFailed = carrierConversationExists && Boolean(carriersConversation.error) && !carrierRetry?.next_retry_at;
    const warehouseAwaits = Boolean(user?.edn_api_account) && WAREHOUSE_VALIDATION_STATUSES.includes(warehouseConversation?.status);

    // « À vérifier »: what waits for a person on this ticket, the blocking first.
    const draftsAwaiting = isEditable
        ? messages.filter((m) => m.status === 'draft'
            && (normalizeMessageContent(m.content).trim() || (messageFiles[m._id] || []).length > 0))
        : [];
    const failedDrafts = draftsAwaiting.filter((m) => m.metadata?.send_policy?.reason === 'send_failed');
    const otherDrafts = draftsAwaiting.filter((m) => !failedDrafts.includes(m));
    const heldConversationIds = new Set(draftsAwaiting
        .flatMap((m) => (Array.isArray(m.held_carrier_contacts) ? m.held_carrier_contacts : []).map((c) => c.conversation_id))
        .filter(Boolean));
    const referenceConflicts = Array.isArray(orderInfo.reference_conflicts) ? orderInfo.reference_conflicts : [];
    const clientMissing = Boolean(contextData?.client_info?.editable) && !contextData?.client_info?.name;

    const scrollToMessage = (messageId) => {
        const node = threadRef.current?.querySelector(`[data-message-id="${messageId}"]`);
        if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const seeLabel = t('ticketWorkspaceUi.attention.see', 'Voir');
    const openLabel = t('ticketWorkspaceUi.attention.open', 'Ouvrir');
    const blockingLabel = t('ticketWorkspaceUi.attention.blocking', 'Bloquant');
    const toValidateLabel = t('ticketWorkspaceUi.attention.toValidate', 'À valider');
    const toCheckLabel = t('ticketWorkspaceUi.attention.toCheck', 'À surveiller');
    const attentionRows = [];
    if (failedDrafts.length > 0) {
        attentionRows.push({
            key: 'draft-failed', tone: 'danger', severity: blockingLabel,
            title: t('ticketWorkspaceUi.attention.draftFailedTitle', 'L’envoi automatique d’une réponse a échoué'),
            meta: t('ticketWorkspaceUi.attention.draftFailedMeta', 'La réponse n’est pas partie : vérifiez-la, puis envoyez-la.'),
            action: { label: seeLabel, onClick: () => scrollToMessage(failedDrafts[0]._id) },
        });
    }
    if (carrierFailed) {
        const diagnosis = getCarrierFailureTexts(t, carriersConversation.failure_diagnosis?.category,
            carriersConversation.carrier_name, carriersConversation.failure_diagnosis?.details);
        attentionRows.push({
            key: 'carrier-failed', tone: 'danger', severity: blockingLabel,
            title: diagnosis.title,
            meta: diagnosis.explanation,
            action: { label: openLabel, onClick: () => selectTab('carriers') },
        });
    }
    if (otherDrafts.length > 0) {
        const heldNames = [...new Set(otherDrafts
            .flatMap((m) => (Array.isArray(m.held_carrier_contacts) ? m.held_carrier_contacts : []))
            .filter((c) => !c.failed).map((c) => c.carrier_name).filter(Boolean))];
        attentionRows.push({
            key: 'drafts', tone: 'warning', severity: toValidateLabel,
            title: otherDrafts.length > 1
                ? t('ticketWorkspaceUi.attention.draftsTitle', '{{count}} réponses de l’IA à valider', { count: otherDrafts.length })
                : t('ticketWorkspaceUi.attention.draftTitle', 'Réponse de l’IA à valider'),
            meta: heldNames.length > 0
                ? t('ticketWorkspaceUi.attention.draftWithCarrier', 'Avec le contact transporteur préparé : {{carriers}}', { carriers: heldNames.join(', ') })
                : t('ticketWorkspaceUi.attention.draftMeta', 'Rédigée le {{date}} · rien ne part au client sans vous', { date: formatShortDateTime(otherDrafts[0].created_at) }),
            action: { label: seeLabel, onClick: () => scrollToMessage(otherDrafts[0]._id) },
        });
    }
    if (carrierAwaits && !carrierFailed) {
        attentionRows.push({
            key: 'carrier-awaiting', tone: 'warning', severity: toValidateLabel,
            title: t('ticketWorkspaceUi.attention.carrierTitle', 'Demande à {{carrier}} à valider', {
                carrier: carriersConversation.carrier_name || t('ticketWorkspaceUi.attention.theCarrier', 'au transporteur'),
            }),
            meta: heldConversationIds.has(carriersConversation._id)
                ? t('ticketWorkspaceUi.attention.carrierHeldMeta', 'Elle part avec la réponse de l’IA si sa case reste cochée.')
                : t('ticketWorkspaceUi.attention.carrierMeta', 'Rien ne part au transporteur sans votre accord.'),
            action: { label: openLabel, onClick: () => selectTab('carriers') },
        });
    }
    if (warehouseAwaits) {
        attentionRows.push({
            key: 'warehouse', tone: 'warning', severity: toValidateLabel,
            title: t('ticketWorkspaceUi.attention.warehouseTitle', 'Demande à l’entrepôt à valider'),
            meta: t('ticketWorkspaceUi.attention.warehouseMeta', 'Rien ne part à l’entrepôt sans votre accord.'),
            action: { label: openLabel, onClick: () => selectTab('warehouse') },
        });
    }
    if (pendingProposals > 0) {
        attentionRows.push({
            key: 'proposals', tone: 'warning', severity: toValidateLabel,
            title: pendingProposals > 1
                ? t('ticketWorkspaceUi.attention.proposalsTitle', '{{count}} actions de l’IA attendent votre accord', { count: pendingProposals })
                : t('ticketWorkspaceUi.attention.proposalTitle', 'Une action de l’IA attend votre accord'),
            meta: t('ticketWorkspaceUi.attention.proposalsMeta', 'Approuvez ou refusez-la dans la vue IA.'),
            action: { label: openLabel, onClick: () => selectTab('ai') },
        });
    }
    if (clientMissing) {
        attentionRows.push({
            key: 'client', tone: 'warning', severity: toCheckLabel,
            title: t('ticketWorkspaceUi.attention.clientTitle', 'Aucun client identifié sur ce ticket'),
            meta: t('ticketWorkspaceUi.attention.clientMeta', 'Les comptes portail du transporteur dépendent du client : choisissez-le.'),
            picker: true,
        });
    }
    if (referenceConflicts.length > 0) {
        attentionRows.push({
            key: 'references', tone: 'warning', severity: toCheckLabel,
            title: t('ticketWorkspaceUi.attention.referencesTitle', 'Les références du ticket ne concordent pas'),
            meta: t('ticketWorkspaceUi.attention.referencesMeta', 'Ce que le client a saisi diffère de ce qui a été trouvé ({{count}}).', { count: referenceConflicts.length }),
            action: { label: seeLabel, onClick: () => selectTab('parcel') },
        });
    }
    const hasAgentNotices = Boolean(ticket.status_proposal?.status)
        || (Array.isArray(ticket.carrier_address_refusals) && ticket.carrier_address_refusals.length > 0);

    // ------------------------------------------------------------------
    // The conversation: latest entries first in view, older on demand
    // ------------------------------------------------------------------
    const threadEntries = [];
    displayedMessages.forEach((message) => {
        if (message.direction === 'is_action' || message.direction === 'is_action_result') {
            const last = threadEntries[threadEntries.length - 1];
            if (last && last.type === 'steps') {
                last.items.push(message);
            } else {
                threadEntries.push({ type: 'steps', key: `steps-${message._id}`, items: [message] });
            }
            return;
        }
        threadEntries.push({ type: 'message', key: message._id, message });
    });
    const mustStayVisible = (entry) => entry.type === 'message'
        && ((entry.message.status === 'draft' && isEditable) || (editingMessage && editingMessage._id === entry.message._id));
    const foldAt = threadHistoryOpen ? 0 : Math.max(0, threadEntries.length - THREAD_KEEP);
    const foldedEntries = threadEntries.slice(0, foldAt).filter((entry) => !mustStayVisible(entry));
    const shownEntries = [...threadEntries.slice(0, foldAt).filter(mustStayVisible), ...threadEntries.slice(foldAt)];
    const conversationCount = displayedMessages.filter((m) => m.direction !== 'is_action' && m.direction !== 'is_action_result').length;

    // ------------------------------------------------------------------
    // Side panel views (≤ 6): the most used first
    // ------------------------------------------------------------------
    const sideTabs = [
        { key: 'carriers', label: t('messageWindow.tabCarrier', 'Transporteur'), title: t('messageWindow.carriersConversation', 'Conversation transporteur'), tour: 'tab-carriers', wrapTour: 'tab-manual', dot: carrierAwaits || carrierFailed },
        user?.edn_api_account && { key: 'warehouse', label: t('messageWindow.tabWarehouse', 'Entrepôt'), title: t('messageWindow.warehouseConversation', 'Conversation entrepôt'), dot: warehouseAwaits },
        { key: 'parcel', label: t('ticketWorkspaceUi.tabs.parcel', 'Colis'), title: t('ticketWorkspaceUi.tabs.parcelTitle', 'Suivi, références et valeurs du colis'), tour: 'tab-track', dot: referenceConflicts.length > 0 },
        { key: 'client', label: t('messageWindow.tabUser', 'Client'), title: t('messageWindow.userInfo', 'Informations utilisateur'), tour: 'tab-user', wrapTour: 'tab-actors', dot: clientMissing },
        { key: 'refunds', label: t('messageWindow.tabRefunds', 'Rembours.'), title: t('refunds.ticketTabTitle', 'Ticket refunds') },
        { key: 'ai', label: t('ticketWorkspaceUi.tabs.ai', 'IA'), title: t('messageWindow.aiRunsTitle', 'Trace des traitements IA de ce ticket'), tour: 'tab-runs', dot: pendingProposals > 0 },
    ].filter(Boolean);
    const currentTab = sideTabs.some((tab) => tab.key === activeTab) ? activeTab : 'carriers';

    const ticketMenuItems = [
        canAct && { label: t('messageWindow.ticketSummary', 'Ticket Summary'), icon: FileText, onClick: () => handleGetTicketSummary(), disabled: loadingSummary },
        { label: t('ticketWorkspaceUi.header.openTimeline', 'Ouvrir la chronologie IA'), icon: Workflow, onClick: () => openAiRun(null, null) },
        isEdnCustomUser && { label: t('messageWindow.statusHistory', 'Status history'), icon: History, onClick: handleGetStatusHistory, disabled: loadingStatusHistory },
        canDisplayColissimoLabels && { label: t('ticketWorkspaceUi.header.colissimoLabel', 'Étiquette retour Colissimo'), icon: Tag, onClick: () => setShowColissimo(true) },
        canAct && { separator: true },
        canAct && { label: t('messageWindow.forwardEmail', 'Forward by email'), icon: Forward, onClick: openForwardModal, disabled: forwardingEmail },
        canAct && { label: t('messageWindow.sendSlackMessage', 'Écrire à l’équipe support'), icon: MessageSquare, onClick: openSlackModal, disabled: sendingSlackMessage },
        canAct && { label: t('messageWindow.reportBug', 'Report a bug'), icon: Bug, onClick: openBugReportModal, disabled: sendingBugReport },
    ];

    const languages = [
        ['original', t('messageWindow.original', 'Original'), t('messageWindow.original', 'Original')],
        ['en', 'EN', 'English'],
        ['fr', 'FR', 'Français'],
        ['es', 'ES', 'Español'],
    ];

    // ------------------------------------------------------------------
    // Pickers (header chips and « À vérifier »)
    // ------------------------------------------------------------------
    const renderClientPicker = (trigger, triggerClassName, align = 'left') => (
        <TicketWorkspaceMenu
            trigger={trigger}
            triggerClassName={triggerClassName}
            triggerTitle={t('messageWindow.clickToChangeClient', 'Click to set the ticket client')}
            disabled={updatingClient}
            align={align}
            heading={t('messageWindow.selectClient', 'Ticket client')}
            onOpen={() => {
                if (!clientOptionsLoaded) fetchClientOptions();
                setClientFilter('');
            }}
            panelClassName="tkw-menu__panel--search"
        >
            {(close) => (clientOptionsLoading ? (
                <div className="tkw-menu__state"><LoaderCircle className="tkw-spin" strokeWidth={2} aria-hidden="true" />{t('messageWindow.loadingClients', 'Loading clients...')}</div>
            ) : clientOptions.length > 0 ? (
                <>
                    <input
                        type="text"
                        className="u-input tkw-menu__search"
                        value={clientFilter}
                        onChange={(e) => setClientFilter(e.target.value)}
                        placeholder={t('messageWindow.searchClient', 'Search a client')}
                        aria-label={t('messageWindow.searchClient', 'Search a client')}
                        autoFocus
                    />
                    <div className="tkw-menu__list">
                        {clientOptions
                            .filter((option) => option.name.toLowerCase().includes(clientFilter.trim().toLowerCase()))
                            .map((option) => {
                                const isActiveOption = option.name === contextData?.client_info?.name;
                                return (
                                    <button
                                        key={option.id}
                                        type="button"
                                        role="menuitem"
                                        className="u-menu__item"
                                        onClick={() => { close(); handleSelectClient(option); }}
                                        disabled={updatingClient || isActiveOption}
                                    >
                                        <span className="tkw-menu__label">{option.name}</span>
                                        {isActiveOption && <Check className="tkw-menu__check" strokeWidth={2} aria-hidden="true" />}
                                    </button>
                                );
                            })}
                    </div>
                </>
            ) : (
                <div className="tkw-menu__state">{t('messageWindow.noClientsConfigured', 'No client configured: add your clients on the Clients page.')}</div>
            ))}
        </TicketWorkspaceMenu>
    );

    const renderTopicPicker = () => (
        <TicketWorkspaceMenu
            trigger={(
                <>
                    <Tag strokeWidth={1.8} aria-hidden="true" />
                    <span className="tkw-chip__text">{currentTopicLabel}</span>
                    {isEditable && <ChevronDown className="tkw-chip__caret" strokeWidth={2} aria-hidden="true" />}
                </>
            )}
            triggerClassName="u-tag tkw-chip tkw-chip--button"
            triggerTitle={t('messageWindow.clickToChangeTopic', 'Click to change topic')}
            disabled={updatingTopic || !isEditable}
            align="left"
            heading={t('messageWindow.selectTopic', 'Select Topic')}
            onOpen={() => { if (availableTopics.length === 0) fetchAvailableTopics(); }}
        >
            {(close) => (topicsLoading ? (
                <div className="tkw-menu__state"><LoaderCircle className="tkw-spin" strokeWidth={2} aria-hidden="true" />{t('messageWindow.loadingTopics', 'Loading topics...')}</div>
            ) : availableTopics.length > 0 ? (
                <div className="tkw-menu__list">
                    {availableTopics.map((topic) => {
                        const isActiveOption = isEdnCustomUser
                            ? Number(currentTopicId) === Number(topic.id)
                            : topic.kind === 'classification'
                                ? ticket.topic_category === topic.value
                                : currentTopicValue === topic.value;
                        return (
                            <button
                                key={topic.id || topic.value}
                                type="button"
                                role="menuitem"
                                className="u-menu__item"
                                onClick={() => { close(); handleUpdateTopic(topic); }}
                                disabled={updatingTopic || isActiveOption}
                            >
                                <span className="tkw-menu__label">{topic.label}</span>
                                {topic.type === 'custom' && <Badge tone="accent">{t('messageWindow.customTopic', 'Custom')}</Badge>}
                                {topic.kind === 'classification' && <Badge tone="info">{t('messageWindow.classificationTopic', 'Topic')}</Badge>}
                                {isActiveOption && <Check className="tkw-menu__check" strokeWidth={2} aria-hidden="true" />}
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div className="tkw-menu__state">{t('messageWindow.noTopicsAvailable', 'No topics available')}</div>
            ))}
        </TicketWorkspaceMenu>
    );

    const renderStatus = () => (isEdnCustomUser ? (
        <TicketWorkspaceMenu
            trigger={(
                <>
                    <span className="u-badge__dot" aria-hidden="true" />
                    <span>{ticketStatusLabel}</span>
                    {updatingEdnStatus
                        ? <LoaderCircle className="tkw-spin" strokeWidth={2} aria-hidden="true" />
                        : <ChevronDown strokeWidth={2} aria-hidden="true" />}
                </>
            )}
            triggerClassName={`u-badge${statusTone ? ` u-badge--${statusTone}` : ''} tkw-status-btn`}
            triggerTitle={t('messageWindow.changeTicketStatus', 'Change ticket status')}
            triggerTour="ticket-status"
            disabled={updatingEdnStatus}
            align="left"
            heading={t('messageWindow.selectStatus', 'Select status')}
            onOpen={() => { if (availableEdnStatuses.length === 0) fetchEdnStatuses(); }}
        >
            {(close) => (ednStatusesLoading ? (
                <div className="tkw-menu__state"><LoaderCircle className="tkw-spin" strokeWidth={2} aria-hidden="true" />{t('messageWindow.loadingStatuses', 'Loading statuses...')}</div>
            ) : (
                <div className="tkw-menu__list">
                    {ednStatusOptions.map((statusOption) => (
                        <button
                            key={statusOption.value}
                            type="button"
                            role="menuitem"
                            className="u-menu__item"
                            onClick={() => { close(); handleUpdateEdnStatus(statusOption.value); }}
                            disabled={updatingEdnStatus || ticket.status === statusOption.value}
                        >
                            <span className="tkw-menu__label">{statusOption.name}</span>
                            {ticket.status === statusOption.value && <Check className="tkw-menu__check" strokeWidth={2} aria-hidden="true" />}
                        </button>
                    ))}
                </div>
            ))}
        </TicketWorkspaceMenu>
    ) : (
        <span className={`u-badge${statusTone ? ` u-badge--${statusTone}` : ''}`} data-tour="ticket-status">
            <span className="u-badge__dot" aria-hidden="true" />
            {ticketStatusLabel}
        </span>
    ));

    const cancelLabel = t('messageWindow.cancel', 'Annuler');
    const closeLabel = t('ticketWorkspaceUi.close', 'Fermer');
    const spinner = <LoaderCircle className="tkw-spin" strokeWidth={2} aria-hidden="true" />;

    return (
        <div className="tkw-page">
            {/* 1 · Header: what the ticket is, its state and identifiers, the verb. */}
            <header className="tkw-header">
                <div className="tkw-header__main">
                    <Link className="tkw-header__back" to={`/dashboard${dashboardSearch}`}>
                        <ArrowLeft strokeWidth={2} aria-hidden="true" />
                        {t('ticketWorkspaceUi.header.back', 'Tickets')}
                    </Link>
                    <h1 className="u-page-title tkw-header__title" title={ticket.subject}>{ticket.subject}</h1>
                    {(ticket.metadata || {}).original_subject && ticket.metadata.original_subject !== ticket.subject && (
                        <div className="ticket-original-subject tkw-header__sub" title={ticket.metadata.original_subject}>
                            {t('messageWindow.originalSubject', "Objet d'origine")} : {ticket.metadata.original_subject}
                        </div>
                    )}
                    {relatedTickets.previous && (
                        <div className="ticket-related tkw-header__sub">
                            <History strokeWidth={1.8} aria-hidden="true" />
                            <span>{t('messageWindow.continuesTicket', 'Suite du ticket')}</span>
                            <Link to={`/tickets/${relatedTickets.previous._id}/messages`} title={relatedTickets.previous.subject}>
                                {relatedTickets.previous.subject || t('messageWindow.untitledTicket', 'Ticket sans objet')}
                            </Link>
                            <span className="ticket-related-meta">
                                {'· '}{getStatusLabel(t, relatedTickets.previous.status)}
                                {relatedTickets.previous.created_at ? ` · ${formatShortDate(relatedTickets.previous.created_at)}` : ''}
                            </span>
                        </div>
                    )}
                    {relatedTickets.next.length > 0 && (
                        <div className="ticket-related tkw-header__sub">
                            <ArrowRight strokeWidth={1.8} aria-hidden="true" />
                            <span>{t('messageWindow.continuedBy', 'Suivi par')}</span>
                            {relatedTickets.next.map((related) => (
                                <Link key={related._id} to={`/tickets/${related._id}/messages`} title={related.subject}>
                                    {related.subject || t('messageWindow.untitledTicket', 'Ticket sans objet')}
                                </Link>
                            ))}
                        </div>
                    )}

                    <div className="tkw-header__meta">
                        {renderStatus()}
                        {ticket.channel_name && (
                            <span className="tkw-meta" title={t('messageWindow.channel', 'Channel')}>
                                <ChannelIcon strokeWidth={1.8} aria-hidden="true" />
                                {ticket.channel_name}
                            </span>
                        )}
                        {ticket.created_at && (
                            <span className="tkw-meta" title={formatDate(ticket.created_at)}>
                                {t('ticketWorkspaceUi.header.receivedOn', 'reçu le {{date}}', { date: formatShortDateTime(ticket.created_at) })}
                            </span>
                        )}
                        <span className="ticket-employee tkw-meta" title={t('messageWindow.employee', 'Employé')}>
                            <User strokeWidth={1.8} aria-hidden="true" />
                            {assignedName || t('messageWindow.notAssigned', 'Non assigné')}
                        </span>
                        {brand?.name && (
                            <span className="tkw-meta" title={t('messageWindow.brand', 'Brand')}>
                                <Store strokeWidth={1.8} aria-hidden="true" />
                                {brand.name}
                            </span>
                        )}
                        {ticket.redirected_to && (
                            <span className="tkw-meta" title={t('messageWindow.redirectedTo', 'Forwarded to')}>
                                <Forward strokeWidth={1.8} aria-hidden="true" />
                                {t('messageWindow.forwardedTo', 'Forwarded to')} {ticket.redirected_to}
                            </span>
                        )}
                    </div>

                    <div className="tkw-header__chips">
                        {contextData && (contextData.client_info?.editable
                            ? renderClientPicker(
                                (
                                    <>
                                        <Building2 strokeWidth={1.8} aria-hidden="true" />
                                        <span className="tkw-chip__text">{contextData.client_info.name || t('messageWindow.selectClientPrompt', 'No client · choose')}</span>
                                        <ChevronDown className="tkw-chip__caret" strokeWidth={2} aria-hidden="true" />
                                    </>
                                ),
                                `u-tag tkw-chip tkw-chip--button${contextData.client_info.name ? '' : ' tkw-chip--missing'}`,
                            )
                            : contextData.client_info?.name && (
                                <span className="u-tag tkw-chip" title={t('messageWindow.clientName', 'Client')}>
                                    <Building2 strokeWidth={1.8} aria-hidden="true" />
                                    <span className="tkw-chip__text">{contextData.client_info.name}</span>
                                </span>
                            ))}
                        {contextData && renderTopicPicker()}
                        {ticket.agent_wakeup?.state === 'scheduled' && ticket.agent_wakeup?.due_at && (
                            // The agent's next wake-up on this case (agent_wakeup_service).
                            <span
                                className="ticket-indicator wakeup u-tag tkw-chip tkw-chip--ai"
                                title={t('messageWindow.wakeupReason', 'Raison : {{reason}}', { reason: ticket.agent_wakeup.reason || '' })}
                            >
                                <AlarmClock strokeWidth={1.8} aria-hidden="true" />
                                <span className="tkw-chip__text">{t('messageWindow.wakeupAt', 'Réveil IA {{date}}', { date: formatShortDateTime(ticket.agent_wakeup.due_at) })}</span>
                            </span>
                        )}
                        {shippingInfo.carrier_name && (
                            <span className="tkw-ident" title={t('messageWindow.carrierName', 'Transporteur')}>
                                <Truck strokeWidth={1.8} aria-hidden="true" />
                                {shippingInfo.carrier_name}
                            </span>
                        )}
                        {trackingNumber && (
                            <span className="tkw-ident">
                                <span className="tkw-ident__label">{t('ticketWorkspaceUi.header.tracking', 'Suivi')}</span>
                                <code className="u-code">{trackingNumber}</code>
                                <button
                                    type="button"
                                    className="u-btn u-btn--ghost u-btn--icon u-btn--sm tkw-ident__copy"
                                    onClick={handleCopyTracking}
                                    title={t('messageWindow.copyTracking', 'Copier le numéro de suivi')}
                                    aria-label={t('messageWindow.copyTracking', 'Copier le numéro de suivi')}
                                >
                                    {trackingCopied ? <Check strokeWidth={2} /> : <Copy strokeWidth={1.8} />}
                                </button>
                            </span>
                        )}
                        {orderReference && (
                            <span className="tkw-ident">
                                <span className="tkw-ident__label">{t('ticketWorkspaceUi.header.order', 'Commande')}</span>
                                <code className="u-code">{orderReference}</code>
                            </span>
                        )}
                        {externalTicketId && (
                            <span className="tkw-ident" title={t('messageWindow.externalTicketId', 'External Ticket ID')}>
                                <span className="tkw-ident__label">{t('ticketWorkspaceUi.header.external', 'Ticket externe')}</span>
                                <code className="u-code">{externalTicketId}</code>
                            </span>
                        )}
                        {shippingInfo.is_delivered === true ? (
                            <Badge tone="success" dot>{t('ticketWorkspaceUi.header.delivered', 'Livré')}</Badge>
                        ) : shippingInfo.is_shipped === true ? (
                            <Badge tone="info" dot>{t('ticketWorkspaceUi.header.shipped', 'Expédié')}</Badge>
                        ) : (shippingInfo.is_shipped === false && trackingNumber) ? (
                            <Badge dot>{t('ticketWorkspaceUi.header.notShipped', 'Pas encore expédié')}</Badge>
                        ) : null}
                    </div>
                </div>

                <div className="u-page-actions tkw-header__actions">
                    {canAct && isEditable && (
                        <TicketWorkspaceMenu
                            trigger={(
                                <>
                                    {assigningTicket ? spinner : <UserPlus strokeWidth={1.8} aria-hidden="true" />}
                                    {t('ticketWorkspaceUi.header.assign', 'Assigner')}
                                </>
                            )}
                            triggerClassName="u-btn u-btn--secondary"
                            triggerTitle={t('messageWindow.assignToEmployee', 'Assigner à un employé')}
                            disabled={assigningTicket}
                            heading={t('messageWindow.assignToEmployee', 'Assigner à un employé')}
                        >
                            {(close) => (employees.length > 0 ? (
                                <div className="tkw-menu__list">
                                    {employees.map((member) => {
                                        const isCurrent = String(member._id) === String(ticket.employee_assigned);
                                        return (
                                            <button
                                                key={member._id}
                                                type="button"
                                                role="menuitem"
                                                className="u-menu__item"
                                                onClick={() => { close(); handleAssignTicket(member._id); }}
                                                disabled={assigningTicket}
                                            >
                                                <span className="tkw-menu__label">{member.name}</span>
                                                {isCurrent && <Check className="tkw-menu__check" strokeWidth={2} aria-hidden="true" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="tkw-menu__state">{t('messageWindow.noEmployeesAvailable', 'Aucun employé disponible')}</div>
                            ))}
                        </TicketWorkspaceMenu>
                    )}
                    {canAct && isEditable && (
                        <button
                            type="button"
                            className="u-btn u-btn--secondary"
                            onClick={openHandoffModal}
                            disabled={handingOffToAI}
                            title={t('ticketWorkspaceUi.header.handoffHint', 'Donner une consigne à l’agent IA, qui reprend le ticket')}
                        >
                            <Sparkles strokeWidth={1.8} aria-hidden="true" />
                            {t('messageWindow.handoffToAI', 'Passer à l’IA')}
                        </button>
                    )}
                    <div className="tkw-header__more" data-tour="ticket-actions">
                        <OverflowMenu label={t('messageWindow.moreActions', "Plus d'actions")} items={ticketMenuItems} />
                    </div>
                    {isEditable && (
                        <button
                            type="button"
                            className="u-btn u-btn--primary"
                            data-tour="resolve-ticket"
                            onClick={handleResolveTicket}
                        >
                            <Check strokeWidth={2} aria-hidden="true" />
                            {t('messageWindow.resolveTicket', 'Résoudre le ticket')}
                        </button>
                    )}
                </div>
            </header>

            <div className="u-well tkw-well">
                {/* 2 · À vérifier: only when something waits for a person. */}
                {(hasAgentNotices || attentionRows.length > 0) && (
                    <div className="tkw-attention">
                        <TicketAgentNotices
                            ticket={ticket}
                            onTicketChange={(changes) => setTicket(prev => (prev ? { ...prev, ...changes } : prev))}
                            onOpenRun={(runId) => openAiRun(runId, null)}
                        />
                        {attentionRows.length > 0 && (
                            <section className="u-card tkw-review" aria-labelledby="tkw-review-title">
                                <div className="tkw-review__head">
                                    <IconChip icon={Inbox} tone="warning" size="sm" />
                                    <h2 id="tkw-review-title" className="tkw-review__title">{t('ticketWorkspaceUi.attention.title', 'À vérifier')}</h2>
                                    <Badge tone="warning">{attentionRows.length}</Badge>
                                </div>
                                <ul className="u-list tkw-review__list">
                                    {attentionRows.map((row) => (
                                        <li key={row.key} className="u-row tkw-review__row">
                                            <Badge tone={row.tone} dot>{row.severity}</Badge>
                                            <div className="u-row__main">
                                                <div className="u-row__title">{row.title}</div>
                                                {row.meta && <div className="u-row__meta u-row__meta--wrap">{row.meta}</div>}
                                            </div>
                                            <div className="u-row__side">
                                                {row.picker ? renderClientPicker(
                                                    (
                                                        <>
                                                            {t('ticketWorkspaceUi.attention.chooseClient', 'Choisir')}
                                                            <ChevronDown strokeWidth={2} aria-hidden="true" />
                                                        </>
                                                    ),
                                                    'u-btn u-btn--secondary u-btn--sm',
                                                    'right',
                                                ) : (
                                                    <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={row.action.onClick}>
                                                        {row.action.label}
                                                        <ChevronRight strokeWidth={2} aria-hidden="true" />
                                                    </button>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}
                    </div>
                )}

                {/* 3 · The work: the conversation and, beside it, the views of the ticket. */}
                <div className="tkw-workspace">
                    <section className="u-card tkw-pane tkw-conversation" aria-labelledby="tkw-thread-title">
                        <div className="tkw-pane__head">
                            <div className="tkw-pane__heading">
                                <h2 id="tkw-thread-title" className="tkw-pane__title">{t('ticketWorkspaceUi.thread.title', 'Conversation')}</h2>
                                <span className="tkw-pane__count">{conversationCount}</span>
                            </div>
                            <div className="tkw-pane__actions">
                                {ticketFiles.length > 0 && (
                                    <TicketWorkspaceMenu
                                        trigger={(
                                            <>
                                                <Paperclip strokeWidth={1.8} aria-hidden="true" />
                                                <span className="tkw-hide-narrow">{t('messageWindow.attachments', 'Pièces jointes')}</span>
                                                <span className="attachments-count tkw-btn-count">{documentTicketFiles.length}</span>
                                            </>
                                        )}
                                        triggerClassName="u-btn u-btn--ghost u-btn--sm attachments-dropdown-trigger"
                                        triggerTitle={t('messageWindow.attachments', 'Pièces jointes')}
                                        heading={`${t('messageWindow.attachments', 'Pièces jointes')} (${documentTicketFiles.length})`}
                                        panelClassName="attachments-dropdown tkw-menu__panel--wide"
                                    >
                                        {(close) => (
                                            <div className="tkw-menu__list">
                                                {[...documentTicketFiles, ...(showDecorativeFiles ? decorativeTicketFiles : [])].map((file, index) => (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        role="menuitem"
                                                        className={`u-menu__item tkw-menu__file ${fileKind(file.mime_type || '')}`}
                                                        onClick={() => { handleFileClick(file); close(); }}
                                                        title={attachmentTooltip(file)}
                                                    >
                                                        <FileGlyph mimeType={file.mime_type || ''} />
                                                        <span className="tkw-menu__file-text">
                                                            <span className="tkw-menu__label">{file.reading?.title || file.name}</span>
                                                            {isDecorativeFile(file) ? (
                                                                <span className="tkw-menu__sub">{t('messageWindow.decorativeFile', 'Signature image')}</span>
                                                            ) : file.reading && (
                                                                <span className="tkw-menu__sub tkw-menu__sub--ai">
                                                                    <Sparkles strokeWidth={1.8} aria-hidden="true" />
                                                                    {file.name} · {t('messageWindow.readByAi', "Lu par l'IA")}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </button>
                                                ))}
                                                {decorativeTicketFiles.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="u-menu__item attachments-decorative-toggle"
                                                        onClick={() => setShowDecorativeFiles((value) => !value)}
                                                        title={t('messageWindow.decorativeFileHint', 'Signature, logo or icon of an e-mail: never read by the AI nor sent to a carrier.')}
                                                        aria-expanded={showDecorativeFiles}
                                                    >
                                                        {showDecorativeFiles ? <EyeOff strokeWidth={1.8} aria-hidden="true" /> : <Eye strokeWidth={1.8} aria-hidden="true" />}
                                                        <span className="tkw-menu__sub">
                                                            {t('messageWindow.decorativeFiles', 'Signature images ({{count}})', { count: decorativeTicketFiles.length })}
                                                        </span>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </TicketWorkspaceMenu>
                                )}
                                <div className="u-seg tkw-lang" role="group" aria-label={t('messageWindow.translationSection', 'Traduction')} data-tour="translate-en">
                                    {languages.map(([value, label, title]) => (
                                        <button
                                            key={value}
                                            type="button"
                                            className={`u-seg__btn${targetLanguage === value ? ' is-active' : ''}`}
                                            aria-pressed={targetLanguage === value}
                                            title={value === 'original' ? title : t('ticketWorkspaceUi.thread.translateTo', 'Traduire en {{language}}', { language: title })}
                                            onClick={() => handleLanguageChange(value)}
                                            disabled={isTranslating}
                                        >
                                            {isTranslating && targetLanguage === value ? spinner : label}
                                        </button>
                                    ))}
                                </div>
                                <OverflowMenu
                                    label={t('ticketWorkspaceUi.thread.more', 'Affichage de la conversation')}
                                    items={[{
                                        label: hideDraftMessages ? t('messageWindow.showDrafts', 'Show drafts') : t('messageWindow.hideDrafts', 'Hide drafts'),
                                        icon: hideDraftMessages ? Eye : EyeOff,
                                        onClick: () => setHideDraftMessages((prev) => !prev),
                                    }]}
                                />
                            </div>
                        </div>

                        <div className="tkw-thread" ref={threadRef}>
                            {foldedEntries.length > 0 && (
                                <button type="button" className="tkw-thread__more" onClick={() => setThreadHistoryOpen(true)} aria-expanded={false}>
                                    <History strokeWidth={1.8} aria-hidden="true" />
                                    {t('ticketWorkspaceUi.thread.showOlder', 'Afficher les messages précédents ({{count}})', { count: foldedEntries.length })}
                                </button>
                            )}
                            {threadHistoryOpen && threadEntries.length > THREAD_KEEP && (
                                <button type="button" className="tkw-thread__more" onClick={() => setThreadHistoryOpen(false)} aria-expanded>
                                    <History strokeWidth={1.8} aria-hidden="true" />
                                    {t('ticketWorkspaceUi.thread.hideOlder', 'Masquer les messages précédents')}
                                </button>
                            )}
                            {shownEntries.map((entry) => (entry.type === 'steps' ? renderAiSteps(entry) : (
                                <React.Fragment key={`fragment-${entry.key}`}>
                                    {renderMessage(entry.message)}
                                    {renderNoteCard(entry.message)}
                                </React.Fragment>
                            )))}
                            {threadEntries.length === 0 && (
                                <EmptyState icon={Inbox} title={t('ticketWorkspaceUi.thread.emptyTitle', 'Aucun message')}>
                                    {hideDraftMessages
                                        ? t('ticketWorkspaceUi.thread.emptyDraftsHidden', 'Les brouillons sont masqués : réaffichez-les depuis « ⋯ ».')
                                        : t('ticketWorkspaceUi.thread.empty', 'Les messages du client et les réponses apparaîtront ici.')}
                                </EmptyState>
                            )}
                            {/* The resolution's justification, written by the AI that resolved the ticket */}
                            {!isEditable && ticket.summary && ticket.summary_meta?.by === 'ai' && (
                                <section className="resolution-note tkw-resolution" aria-label={t('messageWindow.resolutionNote', 'Justificatif de résolution')}>
                                    <header className="tkw-resolution__head">
                                        <Sparkles strokeWidth={1.8} aria-hidden="true" />
                                        <strong>{t('messageWindow.resolutionNote', 'Justificatif de résolution')}</strong>
                                        {ticket.summary_meta?.at && (
                                            <span>{t('messageWindow.resolvedByAiOn', "Résolu par l'IA le {{date}}", { date: formatShortDateTime(ticket.summary_meta.at) })}</span>
                                        )}
                                    </header>
                                    <p><LinkifiedText text={ticket.summary} /></p>
                                </section>
                            )}
                        </div>

                        {isEditable ? (
                            <div className="input-container tkw-composer-wrap">
                                <div className="composer-field tkw-composer" data-tour="composer" onClick={handleComposerClick}>
                                    {pendingFiles.length > 0 && (
                                        <div className="tkw-files">
                                            {pendingFiles.map((file, index) => (
                                                <span key={index} className="tkw-file tkw-file--pending">
                                                    <FileGlyph mimeType={file.mime_type || ''} />
                                                    <span className="tkw-file__name">{file.name}</span>
                                                    <button
                                                        type="button"
                                                        className="tkw-file__remove"
                                                        onClick={() => handleRemovePendingFile(index)}
                                                        title={t('messageWindow.removeFile', 'Remove file')}
                                                        aria-label={t('messageWindow.removeFile', 'Remove file')}
                                                    >
                                                        <X strokeWidth={2} />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <textarea
                                        ref={textareaRef}
                                        value={newMessage}
                                        onChange={handleTextareaChange}
                                        placeholder={t('messageWindow.typeYourResponse')}
                                        aria-label={t('ticketWorkspaceUi.thread.reply', 'Répondre au client')}
                                        className="composer-textarea tkw-composer__input"
                                        rows={1}
                                    />
                                    <div className="tkw-composer__bar">
                                        <button
                                            type="button"
                                            className="u-btn u-btn--ghost u-btn--sm"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingFiles}
                                            title={t('messageWindow.attachFiles')}
                                        >
                                            {uploadingFiles ? spinner : <Paperclip strokeWidth={1.8} aria-hidden="true" />}
                                            <span className="tkw-hide-narrow">{t('messageWindow.attachFiles')}</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="u-btn u-btn--ghost u-btn--sm"
                                            onClick={handleGetAssistantResponse}
                                            disabled={gettingAiHelp}
                                            title={t('ticketWorkspaceUi.thread.aiHelpHint', 'L’assistant de rédaction reformule votre brouillon')}
                                        >
                                            {gettingAiHelp ? spinner : <Sparkles strokeWidth={1.8} aria-hidden="true" />}
                                            <span className="tkw-hide-narrow">{t('messageWindow.aiHelp')}</span>
                                        </button>
                                        <span className="tkw-composer__hint" />
                                        <button
                                            type="button"
                                            className="u-btn u-btn--primary"
                                            onClick={handleSendMessage}
                                            disabled={sendingMessage || (!newMessage.trim() && pendingFiles.length === 0)}
                                        >
                                            {sendingMessage ? spinner : <Send strokeWidth={1.8} aria-hidden="true" />}
                                            {sendingMessage ? t('messageWindow.sending') : t('messageWindow.send')}
                                        </button>
                                    </div>
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelection}
                                    multiple
                                    accept="*/*"
                                    hidden
                                />
                            </div>
                        ) : (
                            <div className="tkw-thread__closed">
                                <CircleCheck strokeWidth={1.8} aria-hidden="true" />
                                {t('ticketWorkspaceUi.thread.readOnly', 'Ticket {{status}} : la conversation est en lecture seule.', { status: ticketStatusLabel.toLowerCase() })}
                            </div>
                        )}
                        <input
                            type="file"
                            ref={draftFileInputRef}
                            onChange={handleDraftFileSelection}
                            multiple
                            accept="*/*"
                            hidden
                        />
                    </section>

                    <aside className="u-card tkw-pane tkw-side" aria-label={t('ticketWorkspaceUi.side.label', 'Détails du ticket')}>
                        <div className="u-tabs tkw-side__tabs" role="tablist" aria-label={t('ticketWorkspaceUi.side.label', 'Détails du ticket')}>
                            {sideTabs.map((tab) => {
                                const button = (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={currentTab === tab.key}
                                        className={`u-tab${currentTab === tab.key ? ' is-active' : ''}`}
                                        data-tour={tab.tour}
                                        title={tab.title}
                                        onClick={() => selectTab(tab.key)}
                                    >
                                        {tab.label}
                                        {tab.dot && (
                                            <>
                                                <span className="tkw-tab-dot" aria-hidden="true" />
                                                <span className="tkw-sr-only">{t('ticketWorkspaceUi.side.awaits', ' (à traiter)')}</span>
                                            </>
                                        )}
                                    </button>
                                );
                                return tab.wrapTour
                                    ? <span key={tab.key} role="presentation" className="tkw-tab-wrap" data-tour={tab.wrapTour}>{button}</span>
                                    : button;
                            })}
                        </div>
                        <div className="tkw-side__body" role="tabpanel">
                            {currentTab === 'carriers' && (
                                <div className="tkw-side__view">
                                    {carrierConversationExists ? (
                                        <div className="tkw-side__toolbar">
                                            <span className="tkw-side__toolbar-text">
                                                {showManualForm
                                                    ? t('ticketWorkspaceUi.carriers.newRequestTitle', 'Nouvelle demande à un transporteur')
                                                    : carriersConversation.conversations_count > 1
                                                        ? t('ticketWorkspaceUi.carriers.latestOf', 'La plus récente de {{count}} demandes transporteur', { count: carriersConversation.conversations_count })
                                                        : t('ticketWorkspaceUi.carriers.request', 'Demande au transporteur')}
                                            </span>
                                            {showManualForm ? (
                                                <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={() => setShowManualForm(false)}>
                                                    <ChevronLeft strokeWidth={2} aria-hidden="true" />
                                                    {t('ticketWorkspaceUi.carriers.backToRequest', 'Retour à la demande')}
                                                </button>
                                            ) : (
                                                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => setShowManualForm(true)}>
                                                    <Plus strokeWidth={2} aria-hidden="true" />
                                                    {t('ticketWorkspaceUi.carriers.newRequest', 'Nouvelle demande')}
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="tkw-side__note">
                                            {t('ticketWorkspaceUi.carriers.none', 'Aucun transporteur n’a encore été contacté pour ce ticket. L’agent IA le fait quand c’est nécessaire ; vous pouvez aussi ouvrir une demande.')}
                                        </p>
                                    )}
                                    {showManualForm && carrierConversationExists ? (
                                        <ManualConversation
                                            onRefresh={fetchCarriersConversation}
                                            ticketFiles={ticketFiles}
                                            ticket={ticket}
                                        />
                                    ) : (
                                        <CarriersConversation
                                            conversation={carrierConversationExists ? carriersConversation : null}
                                            onValidateMessage={handleValidateCarrierMessage}
                                            isValidating={validatingMessage}
                                            onRefresh={fetchCarriersConversation}
                                            ticketFiles={ticketFiles}
                                            ticket={ticket}
                                        />
                                    )}
                                </div>
                            )}
                            {currentTab === 'warehouse' && user && user.edn_api_account && (
                                <WarehouseConversation
                                    ticketId={ticketId}
                                    onRefresh={fetchWarehouseConversation}
                                />
                            )}
                            {currentTab === 'parcel' && (
                                <TicketWorkspaceParcel
                                    loading={contextLoading && !contextData}
                                    shippingInfo={shippingInfo}
                                    orderInfo={orderInfo}
                                    compatibility={compatibilityDetails}
                                    serialNumber={compatibilitySerialNumber}
                                    ednOrder={ednOrder}
                                    ednOrderLoading={orderLoading}
                                />
                            )}
                            {currentTab === 'client' && (
                                <TicketWorkspaceClient
                                    loading={contextLoading && !contextData}
                                    ticketId={ticketId}
                                    customerEmail={ticket.customer_email}
                                    recipient={recipientDetails}
                                    sender={contextData?.sender_info}
                                    client={clientDetails}
                                    shopCustomer={shopCustomer}
                                    shopCustomerLoading={customerLoading}
                                    clientTickets={clientTickets}
                                    clientTicketsLoading={clientTicketsLoading}
                                    clientTicketsError={clientTicketsError}
                                    onOpenTicket={(id) => navigate(`/tickets/${id}/messages`)}
                                    actors={(
                                        <TicketActors
                                            ticketId={ticketId}
                                            ticket={ticket}
                                            messages={messages}
                                            employeeName={employee}
                                            aiAgentName={aiAgentName}
                                            carrierConversation={carriersConversation}
                                        />
                                    )}
                                />
                            )}
                            {currentTab === 'refunds' && (
                                <TicketRefunds ticketId={ticketId} ticket={ticket} />
                            )}
                            {currentTab === 'ai' && (
                                <div className="tkw-side__view">
                                    {/* An approved carrier-contact proposal runs the
                                        contact before answering: reload the carrier
                                        panel like after a draft send (KI-184). */}
                                    <AiProposalsPanel
                                        ticketId={ticketId}
                                        onChanged={() => { fetchCarriersConversation(); fetchProposalCount(); }}
                                    />
                                    <AiRunPanel
                                        ticketId={ticketId}
                                        ticket={ticket}
                                        onOpenRun={(runId) => openAiRun(runId, null)}
                                        onOpenTimeline={() => openAiRun(null, null)}
                                        onRerunDone={() => {
                                            // The enrichment is written synchronously; the AI restart
                                            // finishes in the background a few seconds later.
                                            fetchTicketContext();
                                            setTimeout(() => { fetchMessages(); fetchTicketContext(); }, 6000);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </div>

            {/* ---------------------------------------------------------- dialogs */}

            {showNoteModal && (
                <Modal
                    title={t('messageWindow.addNote', 'Ajouter une note')}
                    description={t('ticketWorkspaceUi.modals.noteDesc', 'Une note est visible par l’équipe uniquement, jamais par le client.')}
                    icon={StickyNote}
                    onClose={closeNoteModal}
                    closeLabel={closeLabel}
                    footer={(
                        <>
                            <button type="button" className="u-btn u-btn--secondary" onClick={closeNoteModal} disabled={addingNote}>{cancelLabel}</button>
                            <button type="button" className="u-btn u-btn--primary" onClick={handleAddNote} disabled={!noteContent.trim() || addingNote}>
                                {addingNote ? spinner : <Plus strokeWidth={2} aria-hidden="true" />}
                                {addingNote ? t('messageWindow.adding') : t('messageWindow.addNote')}
                            </button>
                        </>
                    )}
                >
                    <div className="u-form">
                        {typeof selectedMessageForNote?.content === 'string' && selectedMessageForNote.content.trim() && (
                            <blockquote className="tkw-quote">
                                {selectedMessageForNote.content.substring(0, 160)}
                                {selectedMessageForNote.content.length > 160 && '…'}
                            </blockquote>
                        )}
                        <div className="u-field">
                            <label className="u-label" htmlFor="tkw-note-input">{t('ticketWorkspaceUi.modals.noteLabel', 'Note')}</label>
                            <textarea
                                id="tkw-note-input"
                                value={noteContent}
                                onChange={(e) => setNoteContent(e.target.value)}
                                placeholder={t('messageWindow.noteContentPlaceholder')}
                                className="u-textarea"
                                rows={4}
                                maxLength={500}
                            />
                            <p className="u-help tkw-count">{noteContent.length}/500</p>
                        </div>
                    </div>
                </Modal>
            )}

            {selectedFile && (
                <div className="tkw-viewer" role="dialog" aria-modal="true" aria-label={selectedFile.reading?.title || selectedFile.name}>
                    <div className="tkw-viewer__bar">
                        <span className="tkw-viewer__counter">
                            {selectedAttachmentIndex !== null && totalAttachments > 0
                                ? `${selectedAttachmentIndex + 1} / ${totalAttachments}`
                                : ''}
                        </span>
                        <div className="tkw-viewer__title">
                            <strong>{selectedFile.reading?.title || selectedFile.name}</strong>
                            {selectedFile.reading && (
                                <span className="tkw-viewer__reading">
                                    <Sparkles strokeWidth={1.8} aria-hidden="true" />
                                    {readingDate(selectedFile.reading)
                                        ? t('messageWindow.readByAiOn', "Lu par l'IA le {{date}}", { date: readingDate(selectedFile.reading) })
                                        : t('messageWindow.readByAi', "Lu par l'IA")}
                                    {selectedFile.reading.document_type_label ? ` · ${selectedFile.reading.document_type_label}` : ''}
                                    {` · ${selectedFile.name}`}
                                </span>
                            )}
                        </div>
                        {selectedFile.url && (
                            <a className="u-btn u-btn--secondary u-btn--sm" href={selectedFile.url} target="_blank" rel="noopener noreferrer">
                                {t('messageWindow.downloadFile', 'Télécharger')}
                            </a>
                        )}
                        <button
                            type="button"
                            className="u-btn u-btn--ghost u-btn--icon"
                            onClick={closeFileOverlay}
                            title={t('messageWindow.close', 'Close attachment')}
                            aria-label={t('messageWindow.close', 'Close attachment')}
                        >
                            <X strokeWidth={2} />
                        </button>
                    </div>
                    {selectedFile.reading?.description && (
                        <p className="tkw-viewer__description">{selectedFile.reading.description}</p>
                    )}
                    <div className="tkw-viewer__stage">
                        <button
                            type="button"
                            className="u-btn u-btn--secondary u-btn--icon tkw-viewer__nav"
                            onClick={() => handleAttachmentNavigation(-1)}
                            disabled={selectedAttachmentIndex === null || selectedAttachmentIndex === 0}
                            title={t('messageWindow.previousAttachment', 'Previous attachment')}
                            aria-label={t('messageWindow.previousAttachment', 'Previous attachment')}
                        >
                            <ChevronLeft strokeWidth={2} />
                        </button>
                        <div className={`tkw-viewer__body ${selectedFile.mime_type?.includes('image') ? 'is-image' : 'is-doc'}`}>
                            {selectedFile.mime_type === 'application/pdf' ? (
                                <object data={selectedFile.url} type="application/pdf">
                                    <p>
                                        {t('messageWindow.unableToDisplayFile')}. <a href={selectedFile.url}> {t('messageWindow.downloadFile')}</a>
                                    </p>
                                </object>
                            ) : selectedFile.mime_type?.includes('image') ? (
                                <img src={selectedFile.url} alt={selectedFile.name} />
                            ) : (
                                <iframe src={selectedFile.url} title={selectedFile.name} />
                            )}
                        </div>
                        <button
                            type="button"
                            className="u-btn u-btn--secondary u-btn--icon tkw-viewer__nav"
                            onClick={() => handleAttachmentNavigation(1)}
                            disabled={selectedAttachmentIndex === null || selectedAttachmentIndex >= totalAttachments - 1}
                            title={t('messageWindow.nextAttachment', 'Next attachment')}
                            aria-label={t('messageWindow.nextAttachment', 'Next attachment')}
                        >
                            <ChevronRight strokeWidth={2} />
                        </button>
                    </div>
                </div>
            )}

            {overlayState.show && (
                <Toast
                    tone={overlayState.success ? 'success' : 'danger'}
                    onClose={() => setOverlayState(CLOSED_TOAST)}
                    closeLabel={closeLabel}
                >
                    {overlayState.message}
                </Toast>
            )}

            {ednVisibilityModal.show && (
                <Modal
                    title={t('messageWindow.chooseVisibility', 'Choose message visibility')}
                    description={t('messageWindow.chooseVisibilityDescription', 'Choose who can see this message in EDN before sending.')}
                    icon={Eye}
                    onClose={closeEdnVisibilityModal}
                    closeLabel={closeLabel}
                    footer={<button type="button" className="u-btn u-btn--secondary" onClick={closeEdnVisibilityModal}>{cancelLabel}</button>}
                >
                    <div className="tkw-choices">
                        <button type="button" className="tkw-choice" onClick={() => handleEdnVisibilitySelect('admin')}>
                            <IconChip icon={Shield} tone="neutral" size="sm" />
                            <span className="tkw-choice__text">
                                <strong>{t('messageWindow.visibilityAdmin', 'Admin only')}</strong>
                                <span>{t('ticketWorkspaceUi.modals.visibilityAdminHint', 'Visible par l’équipe dans EDN, pas par le client.')}</span>
                            </span>
                        </button>
                        <button type="button" className="tkw-choice tkw-choice--primary" onClick={() => handleEdnVisibilitySelect('all')}>
                            <IconChip icon={Globe} size="sm" />
                            <span className="tkw-choice__text">
                                <strong>{t('messageWindow.visibilityAll', 'All')}</strong>
                                <span>{t('ticketWorkspaceUi.modals.visibilityAllHint', 'Visible par tous, client compris.')}</span>
                            </span>
                        </button>
                    </div>
                </Modal>
            )}

            {notLeavingConfirm && (
                <ConfirmDialog
                    title={t('ticketWorkspaceUi.modals.notLeavingTitle', 'Envoyer la réponse sans ce contact ?')}
                    message={t('ticketWorkspaceUi.modals.notLeavingMessage',
                        'Cette réponse de l’IA a été écrite avec le contact transporteur préparé dans le même traitement, qui ne partira pas : {{contacts}}. Elle peut l’annoncer au client : vérifiez-la (modifiez-la si besoin).',
                        { contacts: notLeavingLines(notLeavingConfirm.detail) })}
                    confirmLabel={t('ticketWorkspaceUi.modals.notLeavingConfirm', 'Envoyer sans ce contact')}
                    cancelLabel={t('ticketWorkspaceUi.modals.keepDraft', 'Garder le brouillon')}
                    icon={Truck}
                    onCancel={() => setNotLeavingConfirm(null)}
                    onConfirm={() => {
                        const pending = notLeavingConfirm;
                        setNotLeavingConfirm(null);
                        executeSendDraftMessage(pending.messageId, pending.visibility, true);
                    }}
                />
            )}

            {draftToDelete && (
                <ConfirmDialog
                    title={t('ticketWorkspaceUi.modals.deleteDraftTitle', 'Supprimer ce brouillon ?')}
                    message={t('ticketWorkspaceUi.modals.deleteDraftMessage', 'La réponse préparée par l’IA est supprimée : le client ne la recevra pas. Les contacts transporteur préparés avec elle restent dans l’onglet Transporteur.')}
                    confirmLabel={t('ticketWorkspaceUi.modals.deleteDraftConfirm', 'Supprimer le brouillon')}
                    cancelLabel={cancelLabel}
                    icon={Trash2}
                    onCancel={() => setDraftToDelete(null)}
                    onConfirm={() => {
                        const messageId = draftToDelete;
                        setDraftToDelete(null);
                        handleDeleteDraftMessage(messageId);
                    }}
                />
            )}

            {showStatusHistoryModal && (
                <Modal
                    wide
                    title={t('messageWindow.statusHistory', 'Status history')}
                    icon={History}
                    onClose={() => setShowStatusHistoryModal(false)}
                    closeLabel={closeLabel}
                    footer={<button type="button" className="u-btn u-btn--secondary" onClick={() => setShowStatusHistoryModal(false)}>{t('messageWindow.close', 'Close')}</button>}
                >
                    {loadingStatusHistory ? (
                        <div className="tkw-skeleton-stack" aria-busy="true">
                            <span className="u-skeleton tkw-skeleton-line" />
                            <span className="u-skeleton tkw-skeleton-line" />
                            <span className="u-skeleton tkw-skeleton-line tkw-skeleton-line--short" />
                        </div>
                    ) : (
                        <div className="u-form">
                            <div className="tkw-fact-inline">
                                <span className="u-overline">{t('messageWindow.currentStatus', 'Current status')}</span>
                                <strong>{getEdnStatusLabel(statusHistoryData?.current_status || ticket.status)}</strong>
                            </div>
                            {statusHistoryData?.status_history?.length > 0 ? (
                                <Timeline
                                    items={statusHistoryData.status_history.map((entry) => ({
                                        key: entry.id,
                                        icon: ArrowRight,
                                        tone: 'accent',
                                        title: `${getEdnStatusLabel(entry.previous_status)} → ${getEdnStatusLabel(entry.new_status)}`,
                                        meta: [entry.changed_by?.name || entry.changed_by?.email, entry.source].filter(Boolean).join(' · '),
                                        time: formatDate(entry.created_at),
                                    }))}
                                />
                            ) : (
                                <EmptyState icon={History} title={t('messageWindow.noStatusHistory', 'No status history available for this ticket.')} />
                            )}
                        </div>
                    )}
                </Modal>
            )}

            {showHandoffModal && (
                <Modal
                    title={t('messageWindow.handoffToAI', 'Passer à l’IA')}
                    description={t('messageWindow.handoffDescription', 'Provide instructions for the AI agent to handle this ticket. The AI will generate a response based on your instructions.')}
                    icon={Sparkles}
                    onClose={closeHandoffModal}
                    closeLabel={closeLabel}
                    footer={(
                        <>
                            <button type="button" className="u-btn u-btn--secondary" onClick={closeHandoffModal} disabled={handingOffToAI}>{cancelLabel}</button>
                            <button type="button" className="u-btn u-btn--primary" onClick={handleAIHandoff} disabled={!handoffInstructions.trim() || handingOffToAI}>
                                {handingOffToAI ? spinner : <Sparkles strokeWidth={1.8} aria-hidden="true" />}
                                {handingOffToAI ? t('messageWindow.handingOff') : t('messageWindow.handoffToAI')}
                            </button>
                        </>
                    )}
                >
                    <div className="u-form">
                        <div className="tkw-fact-inline">
                            <span className="u-overline">{t('messageWindow.currentlyAssignedTo', 'Assigné à')}</span>
                            <strong>{assignedName || t('messageWindow.notAssigned', 'Non assigné')}</strong>
                        </div>
                        <div className="u-field">
                            <label className="u-label" htmlFor="tkw-handoff-input">{t('ticketWorkspaceUi.modals.handoffLabel', 'Consigne pour l’agent')}</label>
                            <textarea
                                id="tkw-handoff-input"
                                value={handoffInstructions}
                                onChange={(e) => setHandoffInstructions(e.target.value)}
                                placeholder={t('messageWindow.handoffInstructionsPlaceholder', 'Example: Provide a refund for this order, or Please follow up with the carrier about the delivery status...')}
                                className="u-textarea"
                                rows={6}
                                maxLength={2000}
                            />
                            <p className="u-help tkw-count">{handoffInstructions.length}/2000</p>
                        </div>
                    </div>
                </Modal>
            )}

            {showSlackModal && (
                <Modal
                    title={t('messageWindow.sendSlackMessage', 'Écrire à l’équipe support')}
                    description={t('messageWindow.slackDescription', 'Send a message to the support team via Slack. Include any relevant information about this ticket that requires attention.')}
                    icon={MessageSquare}
                    onClose={closeSlackModal}
                    closeLabel={closeLabel}
                    footer={(
                        <>
                            <button type="button" className="u-btn u-btn--secondary" onClick={closeSlackModal} disabled={sendingSlackMessage}>{cancelLabel}</button>
                            <button type="button" className="u-btn u-btn--primary" onClick={handleSendSlackMessage} disabled={!slackMessage.trim() || sendingSlackMessage}>
                                {sendingSlackMessage ? spinner : <Send strokeWidth={1.8} aria-hidden="true" />}
                                {sendingSlackMessage ? t('messageWindow.sending') : t('messageWindow.sendToSlack')}
                            </button>
                        </>
                    )}
                >
                    <div className="u-form">
                        <dl className="tkw-dl">
                            <div><dt>{t('messageWindow.ticketId', 'Ticket ID')}</dt><dd><code className="u-code">{ticketId}</code></dd></div>
                            <div><dt>{t('messageWindow.subject', 'Subject')}</dt><dd>{ticket.subject}</dd></div>
                        </dl>
                        <div className="u-field">
                            <label className="u-label" htmlFor="tkw-slack-input">{t('ticketWorkspaceUi.modals.messageLabel', 'Message')}</label>
                            <textarea
                                id="tkw-slack-input"
                                value={slackMessage}
                                onChange={(e) => setSlackMessage(e.target.value)}
                                placeholder={t('messageWindow.slackMessagePlaceholder', 'Describe the issue or question you need help with from the support team...')}
                                className="u-textarea"
                                rows={6}
                                maxLength={2000}
                            />
                            <p className="u-help tkw-count">{slackMessage.length}/2000</p>
                        </div>
                    </div>
                </Modal>
            )}

            {showBugReportModal && (
                <Modal
                    title={t('messageWindow.reportBug', 'Report a bug')}
                    description={t('messageWindow.bugReportDescription', 'Describe the problem you faced on this ticket. The support team will receive an email with this ticket details and your description.')}
                    icon={Bug}
                    onClose={closeBugReportModal}
                    closeLabel={closeLabel}
                    footer={(
                        <>
                            <button type="button" className="u-btn u-btn--secondary" onClick={closeBugReportModal} disabled={sendingBugReport}>{cancelLabel}</button>
                            <button type="button" className="u-btn u-btn--primary" onClick={handleSendBugReport} disabled={!bugDescription.trim() || sendingBugReport}>
                                {sendingBugReport ? spinner : <Bug strokeWidth={1.8} aria-hidden="true" />}
                                {sendingBugReport ? t('messageWindow.sending') : t('messageWindow.sendBugReport', 'Send bug report')}
                            </button>
                        </>
                    )}
                >
                    <div className="u-form">
                        <dl className="tkw-dl">
                            <div><dt>{t('messageWindow.ticketId', 'Ticket ID')}</dt><dd><code className="u-code">{ticketId}</code></dd></div>
                            <div><dt>{t('messageWindow.subject', 'Subject')}</dt><dd>{ticket.subject}</dd></div>
                        </dl>
                        <div className="u-field">
                            <label className="u-label" htmlFor="tkw-bug-input">{t('ticketWorkspaceUi.modals.bugLabel', 'Ce qui s’est passé')}</label>
                            <textarea
                                id="tkw-bug-input"
                                value={bugDescription}
                                onChange={(e) => setBugDescription(e.target.value)}
                                placeholder={t('messageWindow.bugReportPlaceholder', 'Describe the problem you encountered on this ticket...')}
                                className="u-textarea"
                                rows={6}
                                maxLength={2000}
                            />
                            <p className="u-help tkw-count">{bugDescription.length}/2000</p>
                        </div>
                    </div>
                </Modal>
            )}

            {showForwardModal && (
                <Modal
                    title={t('messageWindow.forwardEmail', 'Forward by email')}
                    description={ticket.outlook_conversation_id
                        ? t('messageWindow.forwardDescription', 'Forward the latest email of this conversation to one or more recipients. Separate multiple addresses with a comma.')
                        : t('messageWindow.forwardPdfDescription', 'This conversation will be sent as a PDF attachment from your connected Outlook account to one or more recipients. Separate multiple addresses with a comma.')}
                    icon={Forward}
                    onClose={closeForwardModal}
                    closeLabel={closeLabel}
                    footer={(
                        <>
                            <button type="button" className="u-btn u-btn--secondary" onClick={closeForwardModal} disabled={forwardingEmail}>{cancelLabel}</button>
                            <button type="button" className="u-btn u-btn--primary" onClick={handleForwardEmail} disabled={!forwardEmailsValid || forwardingEmail}>
                                {forwardingEmail ? spinner : <Forward strokeWidth={1.8} aria-hidden="true" />}
                                {forwardingEmail ? t('messageWindow.sending') : t('messageWindow.forwardSend', 'Forward')}
                            </button>
                        </>
                    )}
                >
                    <div className="u-form">
                        <dl className="tkw-dl">
                            <div><dt>{t('messageWindow.subject', 'Subject')}</dt><dd>{ticket.subject}</dd></div>
                        </dl>
                        <div className="u-field">
                            <label className="u-label" htmlFor="forward-to-input">{t('messageWindow.forwardTo', 'To')} *</label>
                            <input
                                id="forward-to-input"
                                type="text"
                                value={forwardToInput}
                                onChange={(e) => setForwardToInput(e.target.value)}
                                placeholder={t('messageWindow.forwardToPlaceholder', 'recipient@example.com, other@example.com')}
                                className="u-input"
                                disabled={forwardingEmail}
                                aria-invalid={Boolean(forwardToInput.trim() && !forwardEmailsValid)}
                            />
                            {forwardToInput.trim() && !forwardEmailsValid && (
                                <p className="u-error-text" role="alert">{t('messageWindow.forwardInvalidEmails', 'Please enter valid email addresses')}</p>
                            )}
                        </div>
                        <div className="u-field">
                            <label className="u-label" htmlFor="forward-cc-input">{t('messageWindow.forwardCc', 'CC')}</label>
                            <input
                                id="forward-cc-input"
                                type="text"
                                value={forwardCcInput}
                                onChange={(e) => setForwardCcInput(e.target.value)}
                                placeholder={t('messageWindow.forwardCcPlaceholder', 'cc@example.com (optional)')}
                                className="u-input"
                                disabled={forwardingEmail}
                            />
                        </div>
                        <div className="u-field">
                            <label className="u-label" htmlFor="forward-comment-input">{t('messageWindow.forwardComment', 'Comment')}</label>
                            <textarea
                                id="forward-comment-input"
                                value={forwardComment}
                                onChange={(e) => setForwardComment(e.target.value)}
                                placeholder={t('messageWindow.forwardCommentPlaceholder', 'Add an optional comment to include with the forwarded email...')}
                                className="u-textarea"
                                rows={4}
                                maxLength={2000}
                                disabled={forwardingEmail}
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {showSummaryModal && (
                <Modal
                    wide
                    title={t('messageWindow.ticketSummary', 'Ticket Summary')}
                    description={ticket.subject}
                    icon={FileText}
                    onClose={() => setShowSummaryModal(false)}
                    closeLabel={closeLabel}
                    footer={(
                        <>
                            <button type="button" className="u-btn u-btn--secondary" onClick={() => handleGetTicketSummary(true)} disabled={loadingSummary}>
                                {loadingSummary ? spinner : <RefreshCw strokeWidth={1.8} aria-hidden="true" />}
                                {loadingSummary ? t('messageWindow.generatingSummary', 'Generating...') : t('messageWindow.regenerate', 'Regenerate')}
                            </button>
                            <button type="button" className="u-btn u-btn--primary" onClick={() => setShowSummaryModal(false)}>{t('messageWindow.close', 'Close')}</button>
                        </>
                    )}
                >
                    {loadingSummary ? (
                        <div className="tkw-skeleton-stack" aria-busy="true">
                            <span className="tkw-sr-only">{t('messageWindow.generatingSummary', 'Generating summary...')}</span>
                            <span className="u-skeleton tkw-skeleton-line" />
                            <span className="u-skeleton tkw-skeleton-line" />
                            <span className="u-skeleton tkw-skeleton-line tkw-skeleton-line--short" />
                        </div>
                    ) : (
                        <div className="tkw-summary">
                            {ticketSummary.split('\n').map((line, i) => (
                                <p key={i}>{line ? <LinkifiedText text={line} /> : ' '}</p>
                            ))}
                        </div>
                    )}
                </Modal>
            )}

            {showColissimo && canDisplayColissimoLabels && (
                <Drawer
                    wide
                    title={t('ticketWorkspaceUi.header.colissimoLabel', 'Étiquette retour Colissimo')}
                    description={t('ticketWorkspaceUi.modals.colissimoDesc', 'Générez l’étiquette retour de ce ticket, puis téléchargez-la ou envoyez-la au client.')}
                    icon={Tag}
                    onClose={() => setShowColissimo(false)}
                    closeLabel={closeLabel}
                >
                    <ColissimoLabels prefilledTicketId={ticketId} embedded />
                </Drawer>
            )}

            {aiTimelineOpen && (
                <AiTimelineOverlay
                    ticketId={ticketId}
                    ticketSubject={ticket?.subject}
                    focusRunId={aiRunFocus?.runId}
                    focusSpanId={aiRunFocus?.spanId}
                    focusKey={aiRunFocus?.at}
                    onClose={closeAiTimeline}
                />
            )}
        </div>
    );
};

export default MessageWindow;
